import {
  AlertCircle,
  AlertTriangle,
  Banknote,
  BarChart3,
  Calendar,
  CalendarClock,
  Check,
  CheckCircle2,
  CheckSquare,
  ChevronDown,
  ChevronUp,
  Clock,
  Copy,
  Download,
  Edit,
  Eye,
  FileEdit,
  FileSpreadsheet,
  FileText,
  Filter,
  GitBranch,
  ImageIcon,
  Info,
  LayoutTemplate,
  Lock,
  Mail,
  MapPin,
  MessageSquareText,
  MessagesSquare,
  MoreVertical,
  Palette,
  Pause,
  Play,
  Plus,
  Printer,
  Search,
  Settings,
  Share2,
  Shield,
  Sparkles,
  Star,
  Trash2,
  TrendingUp,
  Upload,
  Users,
  Wrench,
  X,
} from "lucide-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useApp } from "@/contexts/AppContext";
import { useUsers } from "@/contexts/UsersContext";
import { useFormatting } from "@/hooks/useFormatting";
import { usePermissions } from "@/hooks/usePermissions";
import { useTerminology } from "@/hooks/useTerminology";
import { getRoleTemplateId } from "@/lib/permissions";
import {
  getCashDurationShortTerm,
  getCashMovementTerms,
  getForecastTerm,
  getScenarioTerm,
  getVarianceTerm,
} from "@/lib/terminology-config";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { ScrollArea } from "../ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import {
  type ReportDefinition,
  ReportPreviewModal,
} from "./ReportPreviewModal";

// ─── Types ───────────────────────────────────────────────────────────────────

interface ScheduledReport {
  id: string;
  reportId: string;
  name: string;
  frequency: string;
  frequencyMinutes: number;
  daysOfWeek: string[];
  startTime: string;
  endTime: string;
  recipients: string;
  format: string;
  status: "active" | "paused";
}

// Role-based access control — keyed by real role IDs (not persona strings)
interface ReportRoleAccess {
  reportId: string;
  roleIds: string[]; // which roles can see this report
  isVisible: boolean;
}

// Custom report — built by users, lives in the Custom Reports workspace
export interface CustomReport {
  id: string;
  name: string;
  description: string;
  createdBy: string; // user ID
  createdByName: string;
  createdAt: Date;
  updatedAt: Date;
  isPublished: boolean; // true = appears in Available Reports
  publishedToRoleIds: string[]; // which roles see it in Available Reports
  isFavourited: boolean;
  metrics: string[];
  sections: CustomReportSection[];
  brandingPreset: string; // ID of a BrandingPreset
  logoId: string | null; // ID of a stored logo
  headerTemplate: string | null;
  footerTemplate: string | null;
  status: "draft" | "published";
  formats: string[];
}

interface CustomReportSection {
  id: string;
  title: string;
  type: "chart" | "table" | "kpi" | "text";
  order: number;
  textContent?: string;
  // Table config
  columns?: string[];
  showTotals?: boolean;
  // Chart config
  chartType?: "bar" | "line" | "area";
  chartMetrics?: string[];
  // KPI card config
  kpiMetrics?: string[];
}

// Global report studio settings
interface BrandingPreset {
  id: string;
  name: string;
  primaryColour: string;
  accentColour: string;
  headerStyle: "minimal" | "standard" | "full";
}

interface StoredLogo {
  id: string;
  name: string;
  dataUrl: string; // base64 — in demo mode only, live mode uses a URL
  uploadedAt: Date;
}

interface HeaderFooterTemplate {
  id: string;
  name: string;
  type: "header" | "footer";
  content: string; // description of layout
}

// Wizard step
type BuilderStep =
  | "name"
  | "metrics"
  | "layout"
  | "branding"
  | "sharing"
  | "preview";

// ─── Constants ───────────────────────────────────────────────────────────────

const DAYS_OF_WEEK = [
  { id: "mon", label: "Mon" },
  { id: "tue", label: "Tue" },
  { id: "wed", label: "Wed" },
  { id: "thu", label: "Thu" },
  { id: "fri", label: "Fri" },
  { id: "sat", label: "Sat" },
  { id: "sun", label: "Sun" },
];

const REPORT_FREQUENCIES = [
  { value: "1440", label: "Daily" },
  { value: "10080", label: "Weekly" },
  { value: "20160", label: "Fortnightly" },
  { value: "43800", label: "Monthly" },
  { value: "131400", label: "Quarterly" },
];

const TIME_OPTIONS = Array.from({ length: 24 }, (_, i) => {
  const hour = i.toString().padStart(2, "0");
  return { value: `${hour}:00`, label: `${hour}:00` };
});

// Available metric groups — all labels derived from terminology
const buildMetricGroups = () => [
  {
    id: "cash-position",
    label: "Cash Position",
    metrics: [
      { id: "opening-balance", label: "Opening Balance" },
      { id: "closing-balance", label: "Closing Balance" },
      { id: "net-movement", label: "Net Movement" },
      { id: "bank-balance", label: "Bank Balance" },
      { id: "available-funds", label: "Available Funds" },
      { id: "facility-headroom", label: "Facility Headroom" },
      { id: "cash-burn-rate", label: "Cash Burn Rate" },
      { id: "days-cash", label: "Days Cash on Hand" },
      { id: "cash-coverage", label: `Cash ${getCashDurationShortTerm()}` },
    ],
  },
  {
    id: "incoming",
    label: getCashMovementTerms().in,
    metrics: [
      { id: "total-incoming", label: `Total ${getCashMovementTerms().in}` },
      { id: "customer-receipts", label: "Customer Receipts" },
      { id: "contract-income", label: "Contract Income" },
      { id: "retention-releases", label: "Retention Releases" },
      { id: "other-income", label: "Other Income" },
      { id: "vat-reclaims", label: "VAT Reclaims" },
      { id: "grant-income", label: "Grant Income" },
      { id: "interest-received", label: "Interest Received" },
      { id: "asset-disposals", label: "Asset Disposals" },
      { id: "insurance-receipts", label: "Insurance Receipts" },
      { id: "intercompany-receipts", label: "Intercompany Receipts" },
    ],
  },
  {
    id: "outgoing",
    label: getCashMovementTerms().out,
    metrics: [
      { id: "total-outgoing", label: `Total ${getCashMovementTerms().out}` },
      { id: "supplier-payments", label: "Supplier Payments" },
      { id: "subcontractor-payments", label: "Subcontractor Payments" },
      { id: "payroll", label: "Payroll" },
      { id: "vat-payments", label: "VAT Payments" },
      { id: "paye-nic", label: "PAYE & NIC" },
      { id: "pension-contributions", label: "Pension Contributions" },
      { id: "materials", label: "Materials" },
      { id: "plant-equipment", label: "Plant & Equipment" },
      { id: "overheads", label: "Overheads" },
      { id: "loan-repayments", label: "Loan Repayments" },
      { id: "interest-paid", label: "Interest Paid" },
      { id: "corporation-tax", label: "Corporation Tax" },
      { id: "capital-expenditure", label: "Capital Expenditure" },
      { id: "lease-payments", label: "Lease Payments" },
      { id: "intercompany-payments", label: "Intercompany Payments" },
    ],
  },
  {
    id: "forecast",
    label: getForecastTerm(true),
    metrics: [
      { id: "forecast-vs-actual", label: `${getForecastTerm(true)} vs Actual` },
      { id: "90-day-position", label: "90-Day Position" },
      { id: "12-month-position", label: "12-Month Position" },
      { id: "3-year-position", label: "3-Year Position" },
      {
        id: "forecast-confidence",
        label: `${getForecastTerm(true)} Confidence`,
      },
      {
        id: "forecast-accuracy",
        label: `${getForecastTerm(true)} Accuracy Score`,
      },
      { id: "forecast-trend", label: `${getForecastTerm(true)} Trend` },
      { id: "weekly-forecast", label: "Weekly Breakdown" },
      { id: "monthly-forecast", label: "Monthly Breakdown" },
      { id: "quarterly-forecast", label: "Quarterly Breakdown" },
    ],
  },
  {
    id: "variance",
    label: getVarianceTerm(true),
    metrics: [
      { id: "variance-summary", label: `${getVarianceTerm(true)} Summary` },
      {
        id: "variance-by-category",
        label: `${getVarianceTerm(true)} by Category`,
      },
      { id: "variance-by-month", label: `${getVarianceTerm(true)} by Month` },
      { id: "variance-trend", label: `${getVarianceTerm(true)} Trend` },
      {
        id: "incoming-variance",
        label: `${getCashMovementTerms().in} ${getVarianceTerm()}`,
      },
      {
        id: "outgoing-variance",
        label: `${getCashMovementTerms().out} ${getVarianceTerm()}`,
      },
      { id: "top-variances", label: `Top 5 ${getVarianceTerm(true)} Items` },
      {
        id: "variance-explanations",
        label: `${getVarianceTerm(true)} Explanations`,
      },
    ],
  },
  {
    id: "scenarios",
    label: getScenarioTerm(true),
    metrics: [
      { id: "active-scenarios", label: `Active ${getScenarioTerm(true)}` },
      {
        id: "scenario-comparison",
        label: `${getScenarioTerm(true)} Comparison`,
      },
      { id: "best-case", label: "Best Case" },
      { id: "worst-case", label: "Worst Case" },
      { id: "base-vs-scenario", label: `Base vs ${getScenarioTerm()}` },
      { id: "scenario-impact", label: `${getScenarioTerm()} Cash Impact` },
      {
        id: "scenario-probability",
        label: `${getScenarioTerm()} Probability Weighting`,
      },
    ],
  },
  {
    id: "liquidity",
    label: "Liquidity & Working Capital",
    metrics: [
      { id: "quick-ratio", label: "Quick Ratio" },
      { id: "current-ratio", label: "Current Ratio" },
      { id: "working-capital", label: "Working Capital" },
      { id: "working-capital-cycle", label: "Working Capital Cycle" },
      { id: "cash-conversion-cycle", label: "Cash Conversion Cycle" },
      { id: "net-working-capital", label: "Net Working Capital" },
    ],
  },
  {
    id: "aged-debtors",
    label: "Aged Debtors",
    metrics: [
      { id: "total-debtors", label: "Total Aged Debtors" },
      { id: "debtors-0-30", label: "0–30 Days" },
      { id: "debtors-31-60", label: "31–60 Days" },
      { id: "debtors-61-90", label: "61–90 Days" },
      { id: "debtors-90-plus", label: "90+ Days" },
      { id: "debtor-days", label: "Debtor Days (DSO)" },
      { id: "overdue-debtors", label: "Overdue Debtors" },
      { id: "top-debtors", label: "Top 10 Debtors" },
      { id: "disputed-invoices", label: "Disputed Invoices" },
    ],
  },
  {
    id: "aged-creditors",
    label: "Aged Creditors",
    metrics: [
      { id: "total-creditors", label: "Total Aged Creditors" },
      { id: "creditors-0-30", label: "0–30 Days" },
      { id: "creditors-31-60", label: "31–60 Days" },
      { id: "creditors-61-90", label: "61–90 Days" },
      { id: "creditors-90-plus", label: "90+ Days" },
      { id: "creditor-days", label: "Creditor Days (DPO)" },
      { id: "overdue-creditors", label: "Overdue Creditors" },
      { id: "top-creditors", label: "Top 10 Creditors" },
    ],
  },
  {
    id: "retentions",
    label: "Retentions",
    metrics: [
      { id: "retentions-held", label: "Total Retentions Held" },
      { id: "retentions-owed", label: "Total Retentions Owed" },
      { id: "retentions-due-month", label: "Retentions Due This Month" },
      { id: "retentions-due-quarter", label: "Retentions Due This Quarter" },
      { id: "retentions-overdue", label: "Overdue Retentions" },
      { id: "retentions-by-contract", label: "Retentions by Contract" },
      { id: "net-retention-position", label: "Net Retention Position" },
    ],
  },
  {
    id: "payroll",
    label: "Payroll & HR",
    metrics: [
      { id: "total-payroll", label: "Total Payroll Cost" },
      { id: "headcount", label: "Headcount" },
      { id: "average-salary", label: "Average Salary Cost" },
      { id: "paye-nic-breakdown", label: "PAYE & NIC Breakdown" },
      { id: "pension-breakdown", label: "Pension Contributions" },
      { id: "payroll-by-department", label: "Payroll by Department" },
      { id: "payroll-trend", label: "Payroll Trend" },
      { id: "overtime-costs", label: "Overtime Costs" },
      { id: "subcontractor-labour", label: "Subcontractor Labour Cost" },
    ],
  },
  {
    id: "contracts",
    label: "Contract Performance",
    metrics: [
      { id: "contract-revenue", label: "Contract Revenue" },
      { id: "contract-costs", label: "Contract Costs" },
      { id: "gross-margin", label: "Gross Margin" },
      { id: "gross-margin-pct", label: "Gross Margin %" },
      { id: "contract-cash-flow", label: "Contract Cash Flow" },
      { id: "contract-wip", label: "Work in Progress (WIP)" },
      { id: "contract-applications", label: "Applications for Payment" },
      { id: "contract-certifications", label: "Certifications" },
      { id: "top-contracts", label: "Top Contracts by Value" },
    ],
  },
  {
    id: "bank-accounts",
    label: "Bank Accounts",
    metrics: [
      { id: "total-bank-position", label: "Total Bank Position" },
      { id: "account-balances", label: "Individual Account Balances" },
      { id: "reconciliation-status", label: "Reconciliation Status" },
      { id: "unreconciled-items", label: "Unreconciled Items" },
      { id: "bank-movements", label: "Bank Movements Summary" },
      { id: "sweep-transactions", label: "Sweep Transactions" },
    ],
  },
  {
    id: "debt-facilities",
    label: "Debt & Facilities",
    metrics: [
      { id: "total-debt", label: "Total Debt" },
      { id: "overdraft-usage", label: "Overdraft Usage" },
      { id: "facility-utilisation", label: "Facility Utilisation %" },
      { id: "available-facility", label: "Available Facility" },
      { id: "debt-repayments", label: "Debt Repayments Schedule" },
      { id: "interest-charges", label: "Interest Charges" },
      { id: "covenant-headroom", label: "Covenant Headroom" },
      { id: "debt-maturity", label: "Debt Maturity Profile" },
    ],
  },
];

// Demo branding presets
const BRANDING_PRESETS: BrandingPreset[] = [
  {
    id: "default",
    name: "Access Default",
    primaryColour: "#0d9488",
    accentColour: "#0284c7",
    headerStyle: "standard",
  },
  {
    id: "minimal",
    name: "Minimal",
    primaryColour: "#374151",
    accentColour: "#6b7280",
    headerStyle: "minimal",
  },
  {
    id: "corporate",
    name: "Corporate",
    primaryColour: "#1e3a5f",
    accentColour: "#c8a951",
    headerStyle: "full",
  },
  {
    id: "modern",
    name: "Modern",
    primaryColour: "#7c3aed",
    accentColour: "#06b6d4",
    headerStyle: "standard",
  },
];

const HEADER_TEMPLATES: HeaderFooterTemplate[] = [
  {
    id: "h1",
    name: "Logo + Company Name + Date",
    type: "header",
    content: "Logo on left, company name centred, report date on right",
  },
  {
    id: "h2",
    name: "Company Name + Report Title",
    type: "header",
    content: "Company name and report title stacked, clean minimal style",
  },
  {
    id: "h3",
    name: "Full Branding Header",
    type: "header",
    content:
      "Full width brand bar with logo, company name, and confidentiality marker",
  },
];

const FOOTER_TEMPLATES: HeaderFooterTemplate[] = [
  {
    id: "f1",
    name: "Page Number + Date",
    type: "footer",
    content: "Centred page number, generation date on right",
  },
  {
    id: "f2",
    name: "Confidentiality + Page Number",
    type: "footer",
    content: "Confidentiality statement on left, page number on right",
  },
  {
    id: "f3",
    name: "Company + Contact + Page",
    type: "footer",
    content: "Company name and contact email, page number on right",
  },
];

// Demo roles — in live mode these come from the roles API
const DEMO_ROLES = [
  { id: "preparer", name: "Accounts Assistant" },
  { id: "manager", name: "Finance Manager" },
  { id: "cfo", name: "CFO" },
  { id: "ceo", name: "CEO" },
  { id: "external", name: "External Board Member" },
  { id: "admin", name: "System Administrator" },
];

// ─── Report catalogue ─────────────────────────────────────────────────────────

// Build the template reports — protected, cloneable, not editable/deletable
const buildTemplateReports = (): (ReportDefinition & {
  icon: React.ReactNode;
  isProtected: true;
  category: string;
  defaultRoleIds: string[];
})[] => [
  {
    id: "cash-flow-statement",
    name: "Cash Flow Statement",
    description: `Detailed cash ${getCashMovementTerms().in.toLowerCase()} and ${getCashMovementTerms().out.toLowerCase()} with period comparisons`,
    category: "financial",
    frequency: "monthly",
    formats: ["PDF", "Excel", "CSV"],
    icon: <TrendingUp className="h-5 w-5" />,
    isProtected: true,
    defaultRoleIds: ["preparer", "manager", "cfo", "ceo", "external"],
  },
  {
    id: "forecast-summary",
    name: `${getForecastTerm(true)} Summary Report`,
    description: `90-day and 12-month ${getForecastTerm(false)} overview with key metrics`,
    category: "forecast",
    frequency: "weekly",
    formats: ["PDF", "Excel", "CSV"],
    icon: <BarChart3 className="h-5 w-5" />,
    isProtected: true,
    defaultRoleIds: ["preparer", "manager", "cfo", "ceo", "external"],
  },
  {
    id: "variance-analysis",
    name: `${getVarianceTerm(true)} Analysis Report`,
    description: `Actual vs ${getForecastTerm(false)} comparison with ${getVarianceTerm(false)} explanations`,
    category: "variance",
    frequency: "monthly",
    formats: ["PDF", "Excel"],
    icon: <AlertTriangle className="h-5 w-5" />,
    isProtected: true,
    defaultRoleIds: ["manager", "cfo"],
  },
  {
    id: "cash-position",
    name: "Daily Cash Position",
    description:
      "Current cash position with upcoming commitments and expected receipts",
    category: "financial",
    frequency: "on-demand",
    formats: ["PDF", "CSV", "Email"],
    icon: <Banknote className="h-5 w-5" />,
    isProtected: true,
    schedulable: false,
    defaultRoleIds: ["preparer", "manager", "cfo", "ceo", "external"],
  },
  {
    id: "scenario-comparison",
    name: `${getScenarioTerm(true)} Comparison Report`,
    description: `Side-by-side comparison of base, conservative, and optimistic ${getForecastTerm(false)} ${getScenarioTerm(false)}`,
    category: "forecast",
    frequency: "on-demand",
    formats: ["PDF", "Excel", "CSV"],
    icon: <FileSpreadsheet className="h-5 w-5" />,
    isProtected: true,
    schedulable: false,
    defaultRoleIds: ["manager", "cfo"],
  },
  {
    id: "executive-summary",
    name: "Executive Summary",
    description: "High-level overview for board meetings and stakeholders",
    category: "financial",
    frequency: "monthly",
    formats: ["PDF"],
    icon: <FileText className="h-5 w-5" />,
    isProtected: true,
    defaultRoleIds: ["cfo", "ceo", "external"],
  },
  {
    id: "liquidity-analysis",
    name: "Liquidity Analysis",
    description: `Working capital, quick ratio, and cash ${getCashDurationShortTerm()} analysis`,
    category: "financial",
    frequency: "on-demand",
    formats: ["PDF", "Excel"],
    icon: <TrendingUp className="h-5 w-5" />,
    isProtected: true,
    schedulable: false,
    defaultRoleIds: ["manager", "cfo"],
  },
  {
    id: "seasonal-trends",
    name: "Seasonal Trends Report",
    description: "Historical patterns and seasonal cash flow analysis",
    category: "variance",
    frequency: "quarterly",
    formats: ["PDF", "Excel"],
    icon: <Calendar className="h-5 w-5" />,
    isProtected: true,
    defaultRoleIds: ["manager", "cfo"],
  },
];

// Admin-only template reports
const buildAdminTemplateReports = (): (ReportDefinition & {
  icon: React.ReactNode;
  isProtected: true;
  category: string;
  defaultRoleIds: string[];
})[] => [
  {
    id: "roles-permissions-audit",
    name: "Roles & Permissions Audit",
    description:
      "Complete matrix of all roles and their assigned permissions — useful for compliance and access reviews",
    category: "admin",
    frequency: "on-demand",
    formats: ["PDF", "Excel"],
    icon: <Shield className="h-5 w-5" />,
    isProtected: true,
    defaultRoleIds: ["admin"],
  },
  {
    id: "data-source-sync-report",
    name: "Data Source Sync Report",
    description:
      "History of all data source synchronisations — timestamps, record counts, errors, and warnings",
    category: "admin",
    frequency: "weekly",
    formats: ["PDF", "CSV"],
    icon: <Wrench className="h-5 w-5" />,
    isProtected: true,
    defaultRoleIds: ["admin"],
  },
  {
    id: "system-activity-log",
    name: "System Activity Log",
    description:
      "Audit trail of all system events — permission changes, configuration updates, and data exports",
    category: "admin",
    frequency: "on-demand",
    formats: ["PDF", "CSV"],
    icon: <Clock className="h-5 w-5" />,
    isProtected: true,
    defaultRoleIds: ["admin"],
  },
  {
    id: "scenario-workflow-audit",
    name: "Scenario Workflow Audit",
    description:
      "Full lifecycle event trail for all scenarios — approvals, rejections, status transitions, and stage progressions",
    category: "admin",
    frequency: "on-demand",
    formats: ["PDF", "CSV"],
    icon: <GitBranch className="h-5 w-5" />,
    isProtected: true,
    defaultRoleIds: ["admin"],
  },
  {
    id: "scenario-discussion-audit",
    name: "Scenario Discussion Audit",
    description:
      "Complete log of all scenario comments and discussion threads — comments, replies, and reactions",
    category: "admin",
    frequency: "on-demand",
    formats: ["PDF", "CSV"],
    icon: <MessageSquareText className="h-5 w-5" />,
    isProtected: true,
    defaultRoleIds: ["admin"],
  },
  {
    id: "actions-workflow-audit",
    name: "Actions Workflow Audit",
    description:
      "Lifecycle event trail for all actions — creation, completion, reassignment, archiving, and reinstatement",
    category: "admin",
    frequency: "on-demand",
    formats: ["PDF", "CSV"],
    icon: <CheckSquare className="h-5 w-5" />,
    isProtected: true,
    defaultRoleIds: ["admin"],
  },
  {
    id: "actions-discussion-audit",
    name: "Actions Discussion Audit",
    description:
      "Complete log of all action comments — who said what, when, on which action",
    category: "admin",
    frequency: "on-demand",
    formats: ["PDF", "CSV"],
    icon: <MessagesSquare className="h-5 w-5" />,
    isProtected: true,
    defaultRoleIds: ["admin"],
  },
];

// Demo scheduled reports seed
const buildScheduledReports = (): ScheduledReport[] => [
  {
    id: "sched-2",
    reportId: "forecast-summary",
    name: `Weekly ${getForecastTerm(true)} Summary`,
    frequency: "Weekly",
    frequencyMinutes: 10080,
    daysOfWeek: ["mon"],
    startTime: "09:00",
    endTime: "09:00",
    recipients: "Finance Team",
    format: "Email (PDF + Excel)",
    status: "active",
  },
  {
    id: "sched-3",
    reportId: "variance-analysis",
    name: `Monthly ${getVarianceTerm(true)} Analysis`,
    frequency: "Monthly",
    frequencyMinutes: 43800,
    daysOfWeek: ["mon", "tue", "wed", "thu", "fri"],
    startTime: "07:00",
    endTime: "07:00",
    recipients: "Board Members",
    format: "PDF",
    status: "active",
  },
];

// Demo report history
const buildReportHistory = () => {
  const now = new Date();
  const daysAgo = (n: number) => {
    const d = new Date(now);
    d.setDate(d.getDate() - n);
    return d;
  };
  const withExpiry = (date: Date) => {
    const expiresAt = new Date(date);
    expiresAt.setDate(expiresAt.getDate() + 30);
    const daysLeft = Math.ceil(
      (expiresAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24),
    );
    return { date, expiresInDays: daysLeft };
  };
  return [
    {
      reportId: "cash-flow-statement",
      name: "Cash Flow Statement",
      ...withExpiry(daysAgo(0)),
      user: "Sarah Mitchell",
      format: "PDF",
      size: "245 KB",
    },
    {
      reportId: "forecast-summary",
      name: `${getForecastTerm(true)} Summary`,
      ...withExpiry(daysAgo(0)),
      user: "Auto-generated",
      format: "Excel",
      size: "1.2 MB",
    },
    {
      reportId: "cash-position",
      name: "Daily Cash Position",
      ...withExpiry(daysAgo(0)),
      user: "Sarah Mitchell",
      format: "PDF",
      size: "156 KB",
    },
    {
      reportId: "variance-analysis",
      name: `${getVarianceTerm(true)} Analysis`,
      ...withExpiry(daysAgo(1)),
      user: "Sarah Mitchell",
      format: "PDF",
      size: "389 KB",
    },
    {
      reportId: "scenario-comparison",
      name: `${getScenarioTerm(true)} Comparison`,
      ...withExpiry(daysAgo(3)),
      user: "Sarah Mitchell",
      format: "Excel",
      size: "892 KB",
    },
    {
      reportId: "executive-summary",
      name: "Executive Summary",
      ...withExpiry(daysAgo(26)),
      user: "Linda Kowalski",
      format: "PDF",
      size: "312 KB",
    },
  ];
};

// Demo custom reports seed — finance persona reports (Sarah Mitchell)
const buildDemoCustomReports = (): CustomReport[] => [
  {
    id: "custom-1",
    name: "Weekly Cash Snapshot",
    description:
      "Quick weekly view of cash position and key movements for internal team review",
    createdBy: "2", // Sarah Mitchell (manager)
    createdByName: "Sarah Mitchell",
    createdAt: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000),
    updatedAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
    isPublished: true,
    publishedToRoleIds: ["preparer", "manager"],
    isFavourited: false,
    metrics: [
      "opening-balance",
      "closing-balance",
      "total-incoming",
      "total-outgoing",
    ],
    sections: [
      { id: "s1", title: "Cash Position", type: "kpi", order: 1 },
      { id: "s2", title: "Movement Summary", type: "chart", order: 2 },
    ],
    brandingPreset: "default",
    logoId: null,
    headerTemplate: "h1",
    footerTemplate: "f1",
    status: "published",
    formats: ["PDF", "Excel"],
  },
  {
    id: "custom-2",
    name: "Board Pack — Cash Section",
    description: `Executive-level cash and ${getForecastTerm(false)} summary formatted for monthly board packs`,
    createdBy: "2",
    createdByName: "Sarah Mitchell",
    createdAt: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
    updatedAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000),
    isPublished: false,
    publishedToRoleIds: [],
    isFavourited: false,
    metrics: [
      "closing-balance",
      "forecast-vs-actual",
      "12-month-position",
      "variance-summary",
    ],
    sections: [
      { id: "s1", title: "Executive Summary", type: "kpi", order: 1 },
      {
        id: "s2",
        title: `${getForecastTerm(true)} Overview`,
        type: "chart",
        order: 2,
      },
      {
        id: "s3",
        title: `${getVarianceTerm(true)} Notes`,
        type: "table",
        order: 3,
      },
    ],
    brandingPreset: "corporate",
    logoId: null,
    headerTemplate: "h3",
    footerTemplate: "f2",
    status: "draft",
    formats: ["PDF"],
  },
];

// Admin-only demo custom reports seed (David Chen)
const buildAdminDemoCustomReports = (): CustomReport[] => [
  {
    id: "admin-custom-1",
    name: "Monthly Access Review",
    description:
      "Custom summary of role changes, permission updates and new user additions for the month",
    createdBy: "7", // David Chen
    createdByName: "David Chen",
    createdAt: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000),
    updatedAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
    isPublished: true,
    publishedToRoleIds: ["admin"],
    isFavourited: false,
    metrics: [],
    sections: [
      { id: "s1", title: "User Changes", type: "table", order: 1 },
      { id: "s2", title: "Permission Changes", type: "table", order: 2 },
    ],
    brandingPreset: "minimal",
    logoId: null,
    headerTemplate: "h2",
    footerTemplate: "f1",
    status: "published",
    formats: ["PDF"],
  },
  {
    id: "admin-custom-2",
    name: "Data Source Health Dashboard",
    description:
      "Draft report combining sync status, error rates and data freshness across all connected sources",
    createdBy: "7",
    createdByName: "David Chen",
    createdAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000),
    updatedAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000),
    isPublished: false,
    publishedToRoleIds: [],
    isFavourited: false,
    metrics: [],
    sections: [
      { id: "s1", title: "Sync Status Overview", type: "kpi", order: 1 },
      { id: "s2", title: "Error Log", type: "table", order: 2 },
    ],
    brandingPreset: "default",
    logoId: null,
    headerTemplate: "h1",
    footerTemplate: "f2",
    status: "draft",
    formats: ["PDF", "CSV"],
  },
];

// ─── Report Schedule Modal ────────────────────────────────────────────────────

type DeliveryMethod = "email" | "sharepoint" | "onedrive" | "network";

const DELIVERY_METHODS: {
  value: DeliveryMethod;
  label: string;
  icon: string;
  locationLabel: string;
  locationPlaceholder: string;
}[] = [
  {
    value: "email",
    label: "Email",
    icon: "✉️",
    locationLabel: "Email addresses",
    locationPlaceholder: "e.g. finance@buildright.co.uk, cfo@buildright.co.uk",
  },
  {
    value: "sharepoint",
    label: "SharePoint",
    icon: "📁",
    locationLabel: "SharePoint URL / Library path",
    locationPlaceholder:
      "e.g. https://buildright.sharepoint.com/sites/Finance/Reports",
  },
  {
    value: "onedrive",
    label: "OneDrive",
    icon: "☁️",
    locationLabel: "OneDrive folder path",
    locationPlaceholder: "e.g. /Finance/Scheduled Reports",
  },
  {
    value: "network",
    label: "Network Drive",
    icon: "🖥️",
    locationLabel: "Network path",
    locationPlaceholder: "e.g. \\\\server\\Finance\\Reports",
  },
];

const REPORT_FORMATS = [
  { value: "PDF", label: "PDF" },
  { value: "Excel", label: "Excel (.xlsx)" },
  { value: "CSV", label: "CSV" },
];

interface RecipientChip {
  id: string;
  type: "email" | "role";
  label: string;
  value: string;
}

// Inline role picker popover for the recipients section
const RolePickerPopover: React.FC<{
  roles: { id: string; name: string }[];
  selectedRoleIds: string[];
  onToggle: (roleId: string, name: string) => void;
}> = ({ roles, selectedRoleIds, onToggle }) => {
  const [roleSearch, setRoleSearch] = useState("");
  const [open, setOpen] = useState(false);
  const filtered = roles.filter((r) =>
    r.name.toLowerCase().includes(roleSearch.toLowerCase()),
  );
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-7 text-xs gap-1">
          <Users className="h-3 w-3" />
          Add Role Group
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-2" align="start">
        <div className="space-y-2">
          <input
            autoFocus
            value={roleSearch}
            onChange={(e) => setRoleSearch(e.target.value)}
            placeholder="Search roles..."
            className="w-full h-8 rounded-md border border-input bg-background px-3 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          />
          <div className="max-h-48 overflow-y-auto space-y-0.5">
            {filtered.length === 0 && (
              <p className="text-xs text-muted-foreground py-2 text-center">
                No roles found
              </p>
            )}
            {filtered.map((role) => {
              const selected = selectedRoleIds.includes(role.id);
              return (
                <button
                  key={role.id}
                  type="button"
                  onClick={() => onToggle(role.id, role.name)}
                  className={`w-full text-left flex items-center gap-2 px-2 py-1.5 rounded text-sm transition-colors ${
                    selected ? "bg-teal-50 text-teal-700" : "hover:bg-muted"
                  }`}
                >
                  <div
                    className={`h-4 w-4 rounded border flex items-center justify-center flex-shrink-0 ${
                      selected
                        ? "bg-teal-600 border-teal-600"
                        : "border-muted-foreground"
                    }`}
                  >
                    {selected && <Check className="h-2.5 w-2.5 text-white" />}
                  </div>
                  {role.name}
                </button>
              );
            })}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

// Report picker dialog — searchable, filterable, scales to hundreds
const ReportPickerDialog: React.FC<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  reports: ReportDefinition[];
  selectedId: string;
  onSelect: (id: string) => void;
}> = ({ open, onOpenChange, reports, selectedId, onSelect }) => {
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const categories = Array.from(
    new Set(
      reports
        .map((r) => (r as ReportDefinition & { category?: string }).category)
        .filter(Boolean),
    ),
  );
  const filtered = reports.filter((r) => {
    const rWithCat = r as ReportDefinition & { category?: string };
    const matchesSearch =
      r.name.toLowerCase().includes(search.toLowerCase()) ||
      (r.description ?? "").toLowerCase().includes(search.toLowerCase());
    const matchesCat =
      categoryFilter === "all" || rWithCat.category === categoryFilter;
    return matchesSearch && matchesCat;
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Select Report</DialogTitle>
          <DialogDescription>Choose the report to schedule</DialogDescription>
        </DialogHeader>
        <div className="flex gap-2 pt-2">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search reports..."
              className="w-full h-9 pl-8 rounded-md border border-input bg-background px-3 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            />
          </div>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-36 h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map((c) => (
                <SelectItem key={c as string} value={c as string}>
                  {(c as string).charAt(0).toUpperCase() +
                    (c as string).slice(1)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1 overflow-y-auto space-y-1 mt-2 min-h-0">
          {filtered.length === 0 && (
            <div className="text-center py-8 text-muted-foreground text-sm">
              No reports match your search
            </div>
          )}
          {filtered.map((r) => {
            const rWithCat = r as ReportDefinition & {
              category?: string;
              isProtected?: boolean;
            };
            const isSelected = r.id === selectedId;
            return (
              <button
                key={r.id}
                type="button"
                onClick={() => {
                  onSelect(r.id);
                  onOpenChange(false);
                }}
                className={`w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-colors ${
                  isSelected
                    ? "bg-teal-50 border-teal-200 text-teal-800"
                    : "border-transparent hover:bg-muted hover:border-border"
                }`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm truncate">
                      {r.name}
                    </span>
                    {rWithCat.isProtected && (
                      <Lock className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                    )}
                  </div>
                  {r.description && (
                    <p className="text-xs text-muted-foreground truncate mt-0.5">
                      {r.description}
                    </p>
                  )}
                </div>
                {rWithCat.category && (
                  <Badge
                    variant="outline"
                    className="text-xs flex-shrink-0 capitalize"
                  >
                    {rWithCat.category}
                  </Badge>
                )}
                {isSelected && (
                  <Check className="h-4 w-4 text-teal-600 flex-shrink-0" />
                )}
              </button>
            );
          })}
        </div>
        <DialogFooter className="pt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

interface ReportScheduleModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editingSchedule?: ScheduledReport | null;
  onSave: (schedule: Omit<ScheduledReport, "id">) => void;
  availableReports: ReportDefinition[];
}

const ReportScheduleModal: React.FC<ReportScheduleModalProps> = ({
  open,
  onOpenChange,
  editingSchedule,
  onSave,
  availableReports,
}) => {
  const schedulable = availableReports.filter((r) => r.schedulable !== false);

  // Report picker
  const [reportPickerOpen, setReportPickerOpen] = useState(false);
  const [selectedReportId, setSelectedReportId] = useState(
    editingSchedule?.reportId ?? schedulable[0]?.id ?? "",
  );
  const selectedReportDef = schedulable.find((r) => r.id === selectedReportId);

  // Schedule
  const [selectedDays, setSelectedDays] = useState<string[]>(
    editingSchedule?.daysOfWeek ?? ["mon", "tue", "wed", "thu", "fri"],
  );
  const [deliveryTime, setDeliveryTime] = useState(
    editingSchedule?.startTime ?? "08:00",
  );
  const [frequency, setFrequency] = useState(
    editingSchedule?.frequencyMinutes?.toString() ?? "1440",
  );

  // Recipients — chips
  const [recipientChips, setRecipientChips] = useState<RecipientChip[]>([]);
  const [emailInput, setEmailInput] = useState("");

  // Format & delivery
  const [format, setFormat] = useState<string>(
    editingSchedule?.format ?? "PDF",
  );
  const [deliveryMethod, setDeliveryMethod] = useState<DeliveryMethod>("email");
  const [location, setLocation] = useState("");

  // Reset on open
  useEffect(() => {
    if (open) {
      setSelectedReportId(
        editingSchedule?.reportId ?? schedulable[0]?.id ?? "",
      );
      setSelectedDays(
        editingSchedule?.daysOfWeek ?? ["mon", "tue", "wed", "thu", "fri"],
      );
      setDeliveryTime(editingSchedule?.startTime ?? "08:00");
      setFrequency(editingSchedule?.frequencyMinutes?.toString() ?? "1440");
      setRecipientChips([]);
      setEmailInput("");
      setFormat(editingSchedule?.format ?? "PDF");
      setDeliveryMethod("email");
      setLocation("");
      setReportPickerOpen(false);
    }
  }, [open]);

  const toggleDay = (dayId: string) => {
    setSelectedDays((prev) =>
      prev.includes(dayId) ? prev.filter((d) => d !== dayId) : [...prev, dayId],
    );
  };

  // Email chip handling
  const addEmailChip = (raw: string) => {
    const emails = raw
      .split(/[,;\s]+/)
      .map((e) => e.trim())
      .filter((e) => e.length > 0);
    const newChips: RecipientChip[] = emails
      .filter((e) => !recipientChips.some((c) => c.value === e))
      .map((e) => ({ id: `email-${e}`, type: "email", label: e, value: e }));
    if (newChips.length > 0)
      setRecipientChips((prev) => [...prev, ...newChips]);
    setEmailInput("");
  };

  const handleEmailKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      if (emailInput.trim()) addEmailChip(emailInput);
    } else if (
      e.key === "Backspace" &&
      !emailInput &&
      recipientChips.length > 0
    ) {
      setRecipientChips((prev) => prev.slice(0, -1));
    }
  };

  const removeChip = (id: string) =>
    setRecipientChips((prev) => prev.filter((c) => c.id !== id));

  const toggleRoleChip = (roleId: string, roleName: string) => {
    const existing = recipientChips.find(
      (c) => c.type === "role" && c.value === roleId,
    );
    if (existing) {
      removeChip(existing.id);
    } else {
      setRecipientChips((prev) => [
        ...prev,
        { id: `role-${roleId}`, type: "role", label: roleName, value: roleId },
      ]);
    }
  };

  const selectedRoleIds = recipientChips
    .filter((c) => c.type === "role")
    .map((c) => c.value);
  const frequencyLabel =
    REPORT_FREQUENCIES.find((f) => f.value === frequency)?.label ?? "";
  const deliveryMethodDef = DELIVERY_METHODS.find(
    (d) => d.value === deliveryMethod,
  )!;

  const getNextDelivery = () => {
    const now = new Date();
    const [h] = deliveryTime.split(":").map(Number);
    if (now.getHours() < h) return `Today at ${deliveryTime}`;
    return `Tomorrow at ${deliveryTime}`;
  };

  const isValid =
    !!selectedReportId &&
    recipientChips.length > 0 &&
    selectedDays.length > 0 &&
    (deliveryMethod === "email" || location.trim().length > 0);

  const handleSave = () => {
    const recipientSummary = recipientChips
      .map((c) => (c.type === "role" ? `[${c.label}]` : c.label))
      .join(", ");
    onSave({
      reportId: selectedReportId,
      name: selectedReportDef?.name ?? "",
      frequency: frequencyLabel,
      frequencyMinutes: Number.parseInt(frequency),
      daysOfWeek: selectedDays,
      startTime: deliveryTime,
      endTime: deliveryTime,
      recipients: recipientSummary,
      format: `${format} → ${deliveryMethodDef.label}${location ? ` (${location})` : ""}`,
      status: "active",
    });
    onOpenChange(false);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              {editingSchedule ? "Edit Schedule" : "New Schedule"}
            </DialogTitle>
            <DialogDescription>
              Configure automatic report generation and delivery
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* ── Report picker ── */}
            <div className="space-y-2">
              <Label className="text-base font-semibold">Report</Label>
              <button
                type="button"
                onClick={() => setReportPickerOpen(true)}
                className="w-full h-9 flex items-center justify-between rounded-md border border-input bg-background px-3 text-sm shadow-sm hover:bg-muted/50 transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              >
                <span
                  className={
                    selectedReportDef
                      ? "text-foreground"
                      : "text-muted-foreground"
                  }
                >
                  {selectedReportDef?.name ?? "Select a report…"}
                </span>
                <Search className="h-4 w-4 text-muted-foreground" />
              </button>
            </div>

            {/* ── Frequency ── */}
            <div className="space-y-2">
              <Label className="text-base font-semibold">Frequency</Label>
              <Select value={frequency} onValueChange={setFrequency}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {REPORT_FREQUENCIES.map((f) => (
                    <SelectItem key={f.value} value={f.value}>
                      {f.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* ── Days ── */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">Days</Label>
              <div className="flex gap-2">
                {DAYS_OF_WEEK.map((day) => (
                  <Button
                    key={day.id}
                    variant={
                      selectedDays.includes(day.id) ? "default" : "outline"
                    }
                    size="sm"
                    onClick={() => toggleDay(day.id)}
                    className={`flex-1 ${
                      selectedDays.includes(day.id)
                        ? "bg-teal-600 hover:bg-teal-700 text-white"
                        : "hover:bg-muted"
                    }`}
                  >
                    {day.label}
                  </Button>
                ))}
              </div>
            </div>

            {/* ── Delivery time ── */}
            <div className="space-y-2">
              <Label className="text-base font-semibold">Delivery Time</Label>
              <Select value={deliveryTime} onValueChange={setDeliveryTime}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIME_OPTIONS.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* ── Recipients ── */}
            <div className="space-y-2">
              <Label className="text-base font-semibold">Recipients</Label>
              <p className="text-xs text-muted-foreground">
                Add individual email addresses and/or role groups. Any
                combination is supported.
              </p>
              {/* Chips display */}
              <div className="min-h-[38px] flex flex-wrap gap-1.5 rounded-md border border-input bg-background px-2 py-1.5">
                {recipientChips.map((chip) => (
                  <span
                    key={chip.id}
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                      chip.type === "role"
                        ? "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300"
                        : "bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300"
                    }`}
                  >
                    {chip.type === "role" ? (
                      <Users className="h-2.5 w-2.5" />
                    ) : (
                      <Mail className="h-2.5 w-2.5" />
                    )}
                    {chip.label}
                    <button
                      type="button"
                      onClick={() => removeChip(chip.id)}
                      className="hover:opacity-70 ml-0.5"
                    >
                      <X className="h-2.5 w-2.5" />
                    </button>
                  </span>
                ))}
              </div>
              {/* Email input + role picker */}
              <div className="flex gap-2">
                <input
                  type="text"
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  onKeyDown={handleEmailKeyDown}
                  onBlur={() => {
                    if (emailInput.trim()) addEmailChip(emailInput);
                  }}
                  placeholder="Type email and press Enter or comma…"
                  className="flex-1 h-8 rounded-md border border-input bg-background px-3 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                />
                <RolePickerPopover
                  roles={DEMO_ROLES}
                  selectedRoleIds={selectedRoleIds}
                  onToggle={toggleRoleChip}
                />
              </div>
              {recipientChips.length === 0 && (
                <p className="text-xs text-amber-600">
                  At least one recipient is required
                </p>
              )}
            </div>

            {/* ── Format ── */}
            <div className="space-y-2">
              <Label className="text-base font-semibold">Output Format</Label>
              <div className="flex gap-2">
                {REPORT_FORMATS.map((f) => (
                  <button
                    key={f.value}
                    type="button"
                    onClick={() => setFormat(f.value)}
                    className={`flex-1 h-9 rounded-md border text-sm font-medium transition-colors ${
                      format === f.value
                        ? "bg-teal-600 border-teal-600 text-white"
                        : "border-input bg-background hover:bg-muted"
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>

            {/* ── Delivery method ── */}
            <div className="space-y-2">
              <Label className="text-base font-semibold">Delivery Method</Label>
              <div className="grid grid-cols-2 gap-2">
                {DELIVERY_METHODS.map((d) => (
                  <button
                    key={d.value}
                    type="button"
                    onClick={() => {
                      setDeliveryMethod(d.value);
                      setLocation("");
                    }}
                    className={`flex items-center gap-2 h-10 px-3 rounded-md border text-sm font-medium transition-colors ${
                      deliveryMethod === d.value
                        ? "bg-teal-600 border-teal-600 text-white"
                        : "border-input bg-background hover:bg-muted"
                    }`}
                  >
                    <span>{d.icon}</span>
                    {d.label}
                  </button>
                ))}
              </div>
            </div>

            {/* ── Location (conditional) ── */}
            <div className="space-y-2">
              <Label className="text-base font-semibold">
                {deliveryMethodDef.locationLabel}
              </Label>
              <input
                type="text"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder={deliveryMethodDef.locationPlaceholder}
                className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              />
              {deliveryMethod !== "email" && !location.trim() && (
                <p className="text-xs text-amber-600">
                  A destination path or URL is required
                </p>
              )}
            </div>

            {/* ── Schedule preview ── */}
            <div className="bg-muted/50 rounded-lg p-4 space-y-2">
              <div className="flex items-start gap-2">
                <Clock className="h-5 w-5 text-teal-600 mt-0.5" />
                <div className="space-y-1">
                  <p className="font-medium">Schedule Preview</p>
                  <p className="text-sm text-muted-foreground">
                    Next delivery:{" "}
                    <span className="font-semibold text-foreground">
                      {getNextDelivery()}
                    </span>
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {frequencyLabel} —{" "}
                    {selectedDays
                      .map((d) => d.charAt(0).toUpperCase() + d.slice(1))
                      .join(", ")}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {format} via {deliveryMethodDef.label}
                    {recipientChips.length > 0 &&
                      ` → ${recipientChips.length} recipient${recipientChips.length > 1 ? "s" : ""}`}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-900 rounded-lg p-4">
              <div className="flex gap-3">
                <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-blue-900 dark:text-blue-100">
                  Scheduled reports are generated automatically and delivered to
                  the specified destination. You can still generate any report
                  manually at any time from the Available Reports tab.
                </p>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={!isValid}
              className="bg-teal-600 hover:bg-teal-700"
            >
              {editingSchedule ? "Save Changes" : "Create Schedule"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Report picker sub-dialog */}
      <ReportPickerDialog
        open={reportPickerOpen}
        onOpenChange={setReportPickerOpen}
        reports={schedulable}
        selectedId={selectedReportId}
        onSelect={setSelectedReportId}
      />
    </>
  );
};

// ─── Delete Confirmation Dialog ───────────────────────────────────────────────

interface DeleteConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  reportName: string;
  isLinkedToRoles: boolean;
  linkedRoleNames: string[];
  onConfirm: () => void;
}

const DeleteConfirmDialog: React.FC<DeleteConfirmDialogProps> = ({
  open,
  onOpenChange,
  reportName,
  isLinkedToRoles,
  linkedRoleNames,
  onConfirm,
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <AlertCircle className="h-5 w-5" />
            Delete Custom Report
          </DialogTitle>
          <DialogDescription>
            You are about to permanently delete <strong>{reportName}</strong>.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {isLinkedToRoles ? (
            <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 space-y-2">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-900 dark:text-amber-100 text-sm">
                    This report is shared with roles
                  </p>
                  <p className="text-sm text-amber-800 dark:text-amber-200 mt-1">
                    Before deleting, you must remove it from Available Reports
                    and unlink it from the following roles:
                  </p>
                  <ul className="mt-2 space-y-1">
                    {linkedRoleNames.map((role) => (
                      <li
                        key={role}
                        className="text-sm font-medium text-amber-900 dark:text-amber-100 flex items-center gap-1.5"
                      >
                        <span className="h-1.5 w-1.5 rounded-full bg-amber-600 flex-shrink-0" />
                        {role}
                      </li>
                    ))}
                  </ul>
                  <p className="text-sm text-amber-800 dark:text-amber-200 mt-2">
                    Go to <strong>Manage → Role Access Control</strong> to
                    unlink this report first.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-sm">This cannot be undone</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    The report and all its configuration will be permanently
                    deleted. Any scheduled deliveries using this report will
                    also be removed.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          {!isLinkedToRoles && (
            <Button
              variant="outline"
              onClick={() => {
                onConfirm();
                onOpenChange(false);
              }}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Yes, delete permanently
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ─── Publish to Available Reports Dialog ─────────────────────────────────────

interface PublishReportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  report: CustomReport | null;
  onPublish: (reportId: string, roleIds: string[]) => void;
  onUnpublish: (reportId: string) => void;
}

const PublishReportDialog: React.FC<PublishReportDialogProps> = ({
  open,
  onOpenChange,
  report,
  onPublish,
  onUnpublish,
}) => {
  const [selectedRoles, setSelectedRoles] = useState<string[]>(
    report?.publishedToRoleIds ?? [],
  );
  const [isPrivate, setIsPrivate] = useState<boolean>(
    report ? (report.publishedToRoleIds ?? []).length === 0 : true,
  );

  // Reset when report changes
  React.useEffect(() => {
    setSelectedRoles(report?.publishedToRoleIds ?? []);
    setIsPrivate(
      report ? (report.publishedToRoleIds ?? []).length === 0 : true,
    );
  }, [report]);

  const toggleRole = (roleId: string) => {
    setSelectedRoles((prev) =>
      prev.includes(roleId)
        ? prev.filter((r) => r !== roleId)
        : [...prev, roleId],
    );
  };

  if (!report) return null;

  return (
    <>
      {open && (
        <style>{`
          [data-radix-dialog-overlay]:last-of-type { z-index: 150 !important; }
          [data-radix-dialog-content]:last-of-type { z-index: 151 !important; }
        `}</style>
      )}
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Share2 className="h-5 w-5" />
              {report.isPublished
                ? "Manage Availability"
                : "Add to Available Reports"}
            </DialogTitle>
            <DialogDescription>
              <strong>{report.name}</strong> — choose which roles can see this
              report in their Available Reports tab.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Private toggle */}
            <label className="flex items-center gap-3 p-3 border-2 rounded-lg cursor-pointer hover:bg-muted/50 transition-colors border-teal-200 bg-teal-50/50">
              <input
                type="checkbox"
                checked={isPrivate}
                onChange={(e) => {
                  setIsPrivate(e.target.checked);
                  if (e.target.checked) setSelectedRoles([]);
                }}
                className="h-4 w-4 rounded border-input"
              />
              <div>
                <p className="text-sm font-semibold">Private — only me</p>
                <p className="text-xs text-muted-foreground">
                  This report will appear in your Available Reports tab only. No
                  one else will see it.
                </p>
              </div>
            </label>

            {/* Roles list — dimmed when private */}
            <div
              className={`space-y-2 transition-opacity ${isPrivate ? "opacity-40 pointer-events-none" : "opacity-100"}`}
            >
              <Label className="text-sm font-semibold">Share with roles</Label>
              <div className="space-y-2 max-h-[220px] overflow-y-auto pr-1">
                {DEMO_ROLES.map((role) => (
                  <label
                    key={role.id}
                    className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-muted/50 transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={selectedRoles.includes(role.id)}
                      onChange={() => toggleRole(role.id)}
                      className="h-4 w-4 rounded border-input"
                    />
                    <span className="text-sm font-medium">{role.name}</span>
                  </label>
                ))}
              </div>
            </div>

            {!isPrivate && selectedRoles.length === 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-700 flex items-center gap-2">
                <Info className="h-4 w-4 flex-shrink-0" />
                Select at least one role, or switch to Private above.
              </div>
            )}
          </div>

          <DialogFooter className="flex-col gap-2 sm:flex-row">
            {report.isPublished && (
              <Button
                variant="outline"
                className="text-destructive border-destructive/30 hover:bg-destructive/10 sm:mr-auto"
                onClick={() => {
                  onUnpublish(report.id);
                  onOpenChange(false);
                }}
              >
                Remove from Available Reports
              </Button>
            )}
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              disabled={!isPrivate && selectedRoles.length === 0}
              onClick={() => {
                onPublish(report.id, isPrivate ? [] : selectedRoles);
                onOpenChange(false);
              }}
              className="bg-teal-600 hover:bg-teal-700"
            >
              <CheckCircle2 className="h-4 w-4 mr-2" />
              {report.isPublished ? "Update Access" : "Publish Report"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

// ─── Clone Picker Dialog ─────────────────────────────────────────────────────

interface ClonePickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  templateReports: ReportDefinition[];
  customReports: CustomReport[];
  onSelect: (
    report: ReportDefinition | CustomReport,
    type: "template" | "custom",
    action: "save" | "edit",
  ) => void;
}

type ClonePickerFilter = "all" | "template" | "published" | "draft";

const ClonePickerDialog: React.FC<ClonePickerDialogProps> = ({
  open,
  onOpenChange,
  templateReports,
  customReports,
  onSelect,
}) => {
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<ClonePickerFilter>("all");
  const [selectedItem, setSelectedItem] = useState<{
    report: ReportDefinition | CustomReport;
    type: "template" | "custom";
  } | null>(null);

  // Reset on open
  useEffect(() => {
    if (open) {
      setSearch("");
      setFilter("all");
      setSelectedItem(null);
    }
  }, [open]);

  const allItems: {
    report: ReportDefinition | CustomReport;
    type: "template" | "custom";
    subType: "template" | "published" | "draft";
  }[] = [
    ...templateReports.map((r) => ({
      report: r,
      type: "template" as const,
      subType: "template" as const,
    })),
    ...customReports.map((r) => ({
      report: r,
      type: "custom" as const,
      subType: (r as CustomReport).isPublished
        ? ("published" as const)
        : ("draft" as const),
    })),
  ];

  const filtered = allItems.filter(({ report, subType }) => {
    const matchesSearch =
      report.name.toLowerCase().includes(search.toLowerCase()) ||
      (report.description ?? "").toLowerCase().includes(search.toLowerCase());
    const matchesFilter = filter === "all" || subType === filter;
    return matchesSearch && matchesFilter;
  });

  const subTypeBadge = (subType: "template" | "published" | "draft") => {
    if (subType === "template")
      return (
        <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 font-medium">
          Template
        </span>
      );
    if (subType === "published")
      return (
        <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium">
          Published
        </span>
      );
    return (
      <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">
        Draft
      </span>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Clone From</DialogTitle>
          <DialogDescription>
            Choose a report to use as your starting point. A copy will be
            created in your workspace.
          </DialogDescription>
        </DialogHeader>

        {/* Search + filter */}
        <div className="flex gap-2 mt-1">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              autoFocus
              placeholder="Search reports..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select
            value={filter}
            onValueChange={(v) => setFilter(v as ClonePickerFilter)}
          >
            <SelectTrigger className="w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="template">Templates</SelectItem>
              <SelectItem value="published">My Published</SelectItem>
              <SelectItem value="draft">My Drafts</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Results list */}
        <div className="mt-2 max-h-80 overflow-y-auto divide-y border rounded-md">
          {filtered.length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground">
              No reports match your search.
            </div>
          ) : (
            filtered.map(({ report, type, subType }) => {
              const isSelected = selectedItem?.report.id === report.id;
              return (
                <button
                  key={report.id}
                  onClick={() => setSelectedItem({ report, type })}
                  className={`w-full text-left px-4 py-3 transition-colors flex items-start gap-3 ${
                    isSelected
                      ? "bg-primary/10 border-l-2 border-primary"
                      : "hover:bg-muted/50 border-l-2 border-transparent"
                  }`}
                >
                  <Copy
                    className={`h-4 w-4 mt-0.5 shrink-0 ${isSelected ? "text-primary" : "text-muted-foreground"}`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span
                        className={`text-sm font-medium truncate ${isSelected ? "text-primary" : ""}`}
                      >
                        {report.name}
                      </span>
                      {subTypeBadge(subType)}
                    </div>
                    {report.description && (
                      <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
                        {report.description}
                      </p>
                    )}
                  </div>
                  {isSelected && (
                    <Check className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                  )}
                </button>
              );
            })
          )}
        </div>

        {/* Selection hint */}
        {!selectedItem && (
          <p className="text-xs text-muted-foreground text-center -mt-1">
            Select a report above to see your options
          </p>
        )}

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="outline"
            disabled={!selectedItem}
            onClick={() => {
              if (selectedItem) {
                onSelect(selectedItem.report, selectedItem.type, "save");
                onOpenChange(false);
              }
            }}
          >
            <Copy className="h-4 w-4 mr-2" />
            Save Copy
          </Button>
          <Button
            disabled={!selectedItem}
            onClick={() => {
              if (selectedItem) {
                onSelect(selectedItem.report, selectedItem.type, "edit");
                onOpenChange(false);
              }
            }}
          >
            <FileEdit className="h-4 w-4 mr-2" />
            Open in Editor
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ─── Clone Template Dialog ────────────────────────────────────────────────────

interface CloneTemplateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  template: ReportDefinition | null;
  onClone: (name: string, description: string) => void;
}

const CloneTemplateDialog: React.FC<CloneTemplateDialogProps> = ({
  open,
  onOpenChange,
  template,
  onClone,
}) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  React.useEffect(() => {
    if (template) {
      setName(`Copy of ${template.name}`);
      setDescription(template.description ?? "");
    }
  }, [template]);

  if (!template) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Copy className="h-5 w-5" />
            Clone Template
          </DialogTitle>
          <DialogDescription>
            Create an editable copy of <strong>{template.name}</strong>. The
            original template stays protected and unchanged.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="clone-name">Report Name</Label>
            <Input
              id="clone-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter a name for your report"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="clone-description">Description</Label>
            <textarea
              id="clone-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe what this report is for"
              rows={3}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring resize-none"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={!name.trim()}
            onClick={() => {
              onClone(name.trim(), description.trim());
              onOpenChange(false);
            }}
            className="bg-teal-600 hover:bg-teal-700"
          >
            <Copy className="h-4 w-4 mr-2" />
            Create Copy
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ─── Metrics Step Component ───────────────────────────────────────────────────

interface MetricGroup {
  id: string;
  label: string;
  metrics: { id: string; label: string }[];
}

interface MetricsStepProps {
  metricGroups: MetricGroup[];
  selectedMetrics: string[];
  toggleMetric: (id: string) => void;
}

const MetricsStep: React.FC<MetricsStepProps> = ({
  metricGroups,
  selectedMetrics,
  toggleMetric,
}) => {
  const [search, setSearch] = useState("");
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const toggleCollapse = (id: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const filtered = search.trim()
    ? metricGroups
        .map((g) => ({
          ...g,
          metrics: g.metrics.filter((m) =>
            m.label.toLowerCase().includes(search.toLowerCase()),
          ),
        }))
        .filter((g) => g.metrics.length > 0)
    : metricGroups;

  const allMetricIds = metricGroups.flatMap((g) => g.metrics.map((m) => m.id));
  const allSelected = allMetricIds.every((id) => selectedMetrics.includes(id));
  const noneSelected = selectedMetrics.length === 0;

  const selectAll = () =>
    allMetricIds.forEach((id) => {
      if (!selectedMetrics.includes(id)) toggleMetric(id);
    });
  const clearAll = () => [...selectedMetrics].forEach((id) => toggleMetric(id));

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        Select the individual metrics to include in this report. All labels
        honour your organisation's terminology settings.
      </p>

      {/* Search + bulk actions */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search metrics..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 h-9 text-sm"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch("")}
              className="absolute right-2.5 top-2.5 text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={selectAll}
          disabled={allSelected}
          className="text-xs whitespace-nowrap"
        >
          Select All
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={clearAll}
          disabled={noneSelected}
          className="text-xs whitespace-nowrap"
        >
          Clear All
        </Button>
      </div>

      {/* Selected chips */}
      {selectedMetrics.length > 0 && (
        <div className="flex items-center gap-1.5 flex-wrap p-2 bg-muted/30 rounded-lg border">
          <span className="text-xs font-medium text-muted-foreground shrink-0">
            {selectedMetrics.length} selected:
          </span>
          {selectedMetrics.map((mId) => {
            const label =
              metricGroups.flatMap((g) => g.metrics).find((m) => m.id === mId)
                ?.label ?? mId;
            return (
              <span
                key={mId}
                className="flex items-center gap-1 text-xs bg-teal-50 text-teal-700 border border-teal-200 rounded-full px-2 py-0.5 dark:bg-teal-900/20 dark:text-teal-400 dark:border-teal-800"
              >
                {label}
                <button type="button" onClick={() => toggleMetric(mId)}>
                  <X className="h-3 w-3" />
                </button>
              </span>
            );
          })}
        </div>
      )}

      {/* No search results */}
      {filtered.length === 0 && (
        <div className="text-center py-8 text-sm text-muted-foreground">
          No metrics match "{search}"
        </div>
      )}

      {/* Metric groups — collapsible, scrollable */}
      <div className="overflow-y-auto max-h-[340px] pr-1 space-y-2 scrollbar-thin scrollbar-thumb-muted-foreground/20 scrollbar-track-transparent">
        {filtered.map((group) => {
          const isCollapsed = collapsed.has(group.id) && !search;
          const groupSelected = group.metrics.filter((m) =>
            selectedMetrics.includes(m.id),
          ).length;
          return (
            <div key={group.id} className="border rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => !search && toggleCollapse(group.id)}
                className="w-full flex items-center justify-between bg-muted/50 px-4 py-2.5 hover:bg-muted/70 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <p className="text-sm font-semibold">{group.label}</p>
                  {groupSelected > 0 && (
                    <span className="text-xs bg-teal-100 text-teal-700 rounded-full px-1.5 py-0.5 dark:bg-teal-900/30 dark:text-teal-400">
                      {groupSelected}/{group.metrics.length}
                    </span>
                  )}
                </div>
                {!search && (
                  <ChevronDown
                    className={`h-4 w-4 text-muted-foreground transition-transform ${isCollapsed ? "" : "rotate-180"}`}
                  />
                )}
              </button>
              {!isCollapsed && (
                <div className="p-3 grid grid-cols-2 gap-1.5">
                  {group.metrics.map((metric) => (
                    <label
                      key={metric.id}
                      className="flex items-center gap-2 p-2 rounded-md cursor-pointer hover:bg-muted/50 transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={selectedMetrics.includes(metric.id)}
                        onChange={() => toggleMetric(metric.id)}
                        className="h-4 w-4 rounded border-input accent-teal-600"
                      />
                      <span className="text-sm">{metric.label}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── Custom Report Builder Wizard ─────────────────────────────────────────────

interface BuilderWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editingReport: CustomReport | null;
  cloneSource: ReportDefinition | null;
  onSave: (
    report: Omit<
      CustomReport,
      "id" | "createdAt" | "updatedAt" | "createdBy" | "createdByName"
    >,
  ) => void;
}

const BuilderWizard: React.FC<BuilderWizardProps> = ({
  open,
  onOpenChange,
  editingReport,
  cloneSource,
  onSave,
}) => {
  // Subscribe to terminology changes so the metric group labels recompute
  // when an admin updates terminology mid-session.
  useTerminology();
  const metricGroups = buildMetricGroups();

  const [step, setStep] = useState<BuilderStep>("name");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [selectedMetrics, setSelectedMetrics] = useState<string[]>([]);
  const [sections, setSections] = useState<CustomReportSection[]>([]);
  const [brandingPreset, setBrandingPreset] = useState("default");
  const [logoId, setLogoId] = useState<string | null>(null);
  const [headerTemplate, setHeaderTemplate] = useState<string | null>("h1");
  const [footerTemplate, setFooterTemplate] = useState<string | null>("f1");
  const [selectedFormats, setSelectedFormats] = useState<string[]>(["PDF"]);

  // Initialise from editing report or clone source
  React.useEffect(() => {
    if (!open) {
      // Reset on close
      setStep("name");
      setName("");
      setDescription("");
      setSelectedMetrics([]);
      setSections([]);
      setBrandingPreset("default");
      setLogoId(null);
      setHeaderTemplate("h1");
      setFooterTemplate("f1");
      setSelectedFormats(["PDF"]);
      return;
    }
    if (editingReport) {
      setName(editingReport.name);
      setDescription(editingReport.description);
      setSelectedMetrics(editingReport.metrics);
      setSections(editingReport.sections);
      setBrandingPreset(editingReport.brandingPreset);
      setLogoId(editingReport.logoId);
      setHeaderTemplate(editingReport.headerTemplate);
      setFooterTemplate(editingReport.footerTemplate);
      setSelectedFormats(editingReport.formats);
      setStep("name");
    } else if (cloneSource) {
      setName(`Copy of ${cloneSource.name}`);
      setDescription(cloneSource.description ?? "");
      // Pre-tick sensible default metrics based on the template's category
      const defaultMetricsByCategory: Record<string, string[]> = {
        Financial: [
          "opening-balance",
          "closing-balance",
          "net-movement",
          "total-incoming",
          "total-outgoing",
          "bank-balance",
        ],
        Forecast: [
          "90-day-position",
          "12-month-position",
          "3-year-position",
          "forecast-vs-actual",
          "forecast-confidence",
        ],
        "Variance Analysis": [
          "variance-summary",
          "variance-by-category",
          "variance-trend",
          "incoming-variance",
          "outgoing-variance",
        ],
        Liquidity: [
          "quick-ratio",
          "current-ratio",
          "working-capital",
          "cash-coverage",
          "days-cash",
        ],
        Payroll: [
          "total-payroll",
          "headcount",
          "paye-nic-breakdown",
          "pension-breakdown",
          "payroll-trend",
        ],
        "Aged Debtors": [
          "total-debtors",
          "debtors-0-30",
          "debtors-31-60",
          "debtors-61-90",
          "debtors-90-plus",
          "debtor-days",
        ],
        "Aged Creditors": [
          "total-creditors",
          "creditors-0-30",
          "creditors-31-60",
          "creditors-61-90",
          "creditors-90-plus",
          "creditor-days",
        ],
        Retentions: [
          "retentions-held",
          "retentions-owed",
          "retentions-due-month",
          "retentions-due-quarter",
        ],
        Contracts: [
          "contract-revenue",
          "contract-costs",
          "gross-margin",
          "gross-margin-pct",
          "contract-cash-flow",
        ],
      };
      const preSelected = defaultMetricsByCategory[cloneSource.category] ?? [
        "opening-balance",
        "closing-balance",
        "net-movement",
      ];
      setSelectedMetrics(preSelected);
      setSections([]);
      setStep("name");
    }
  }, [open, editingReport, cloneSource]);

  const steps: { id: BuilderStep; label: string }[] = [
    { id: "name", label: "Name" },
    { id: "metrics", label: "Metrics" },
    { id: "layout", label: "Layout" },
    { id: "branding", label: "Branding" },
    { id: "preview", label: "Preview" },
  ];

  const currentStepIndex = steps.findIndex((s) => s.id === step);

  const toggleMetric = (metricId: string) => {
    setSelectedMetrics((prev) =>
      prev.includes(metricId)
        ? prev.filter((m) => m !== metricId)
        : [...prev, metricId],
    );
  };

  const toggleFormat = (fmt: string) => {
    setSelectedFormats((prev) =>
      prev.includes(fmt) ? prev.filter((f) => f !== fmt) : [...prev, fmt],
    );
  };

  const addSection = () => {
    setSections((prev) => [
      ...prev,
      {
        id: `section-${Date.now()}`,
        title: `Section ${prev.length + 1}`,
        type: "kpi",
        order: prev.length + 1,
      },
    ]);
  };

  const removeSection = (id: string) => {
    setSections((prev) => prev.filter((s) => s.id !== id));
  };

  const moveSectionUp = (idx: number) => {
    if (idx === 0) return;
    setSections((prev) => {
      const next = [...prev];
      [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
      return next.map((s, i) => ({ ...s, order: i + 1 }));
    });
  };

  const moveSectionDown = (idx: number) => {
    setSections((prev) => {
      if (idx === prev.length - 1) return prev;
      const next = [...prev];
      [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
      return next.map((s, i) => ({ ...s, order: i + 1 }));
    });
  };

  const handleSave = () => {
    onSave({
      name,
      description,
      isPublished: editingReport?.isPublished ?? false,
      publishedToRoleIds: editingReport?.publishedToRoleIds ?? [],
      isFavourited: editingReport?.isFavourited ?? false,
      metrics: selectedMetrics,
      sections,
      brandingPreset,
      logoId,
      headerTemplate,
      footerTemplate,
      status: "draft",
      formats: selectedFormats,
    });
    onOpenChange(false);
  };

  const preset = BRANDING_PRESETS.find((p) => p.id === brandingPreset);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col p-0 gap-0">
        {/* Header */}
        <div className="px-6 pt-6 pb-4 border-b">
          <DialogTitle className="flex items-center gap-2 text-lg">
            <LayoutTemplate className="h-5 w-5" />
            {editingReport ? "Edit Report" : "New Custom Report"}
          </DialogTitle>
          <DialogDescription className="mt-1">
            {editingReport
              ? `Editing "${editingReport.name}"`
              : cloneSource
                ? `Starting from template: ${cloneSource.name}`
                : "Build a report from scratch"}
          </DialogDescription>

          {/* Step progress */}
          <div className="flex items-center gap-1 mt-4">
            {steps.map((s, idx) => (
              <React.Fragment key={s.id}>
                <button
                  type="button"
                  onClick={() => {
                    if (idx < currentStepIndex || name.trim()) setStep(s.id);
                  }}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                    s.id === step
                      ? "bg-teal-600 text-white"
                      : idx < currentStepIndex
                        ? "bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400"
                        : "text-muted-foreground"
                  }`}
                >
                  {idx < currentStepIndex && (
                    <CheckCircle2 className="h-3 w-3" />
                  )}
                  {s.label}
                </button>
                {idx < steps.length - 1 && (
                  <div
                    className={`flex-1 h-px ${idx < currentStepIndex ? "bg-teal-300" : "bg-muted"}`}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Body */}
        <ScrollArea className="flex-1 px-6 py-4">
          {/* Step: Name */}
          {step === "name" && (
            <div className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="report-name" className="font-semibold">
                  Report Name *
                </Label>
                <Input
                  id="report-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Monthly Board Pack — Cash Section"
                  className="text-base"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="report-description" className="font-semibold">
                  Description
                </Label>
                <textarea
                  id="report-description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="What is this report for? Who is the audience?"
                  rows={3}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring resize-none"
                />
              </div>
              <div className="space-y-2">
                <Label className="font-semibold">Output Formats</Label>
                <div className="flex flex-wrap gap-2">
                  {["PDF", "Excel", "CSV", "Email"].map((fmt) => (
                    <button
                      key={fmt}
                      type="button"
                      onClick={() => toggleFormat(fmt)}
                      className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                        selectedFormats.includes(fmt)
                          ? "bg-teal-600 text-white border-teal-600"
                          : "border-input text-muted-foreground hover:border-teal-400"
                      }`}
                    >
                      {fmt}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step: Metrics */}
          {step === "metrics" && (
            <MetricsStep
              metricGroups={metricGroups}
              selectedMetrics={selectedMetrics}
              toggleMetric={toggleMetric}
            />
          )}

          {/* Step: Layout */}
          {step === "layout" &&
            (() => {
              // Build smart suggestions from selected metrics
              const metricGroups = buildMetricGroups();
              const suggestions: Array<{
                title: string;
                type: CustomReportSection["type"];
              }> = [];
              const groupTypeMap: Record<string, CustomReportSection["type"]> =
                {
                  "cash-position": "kpi",
                  incoming: "table",
                  outgoing: "table",
                  forecast: "chart",
                  variance: "chart",
                  scenarios: "chart",
                  liquidity: "kpi",
                  "aged-debtors": "table",
                  "aged-creditors": "table",
                  retentions: "table",
                  payroll: "table",
                  contracts: "table",
                  "bank-accounts": "kpi",
                  "debt-facilities": "kpi",
                };
              for (const group of metricGroups) {
                const selectedInGroup = group.metrics.filter((m) =>
                  selectedMetrics.includes(m.id),
                );
                if (selectedInGroup.length === 0) continue;
                // Use individual metric name if only one selected from group, group label if multiple
                const title =
                  selectedInGroup.length === 1
                    ? selectedInGroup[0].label
                    : group.label;
                if (!sections.some((s) => s.title === title)) {
                  suggestions.push({
                    title,
                    type: groupTypeMap[group.id] ?? "table",
                  });
                }
              }

              const DYNAMIC_TOKENS = [
                { label: "Company Name", value: "{Company Name}" },
                { label: "Report Name", value: "{Report Name}" },
                { label: "Date Generated", value: "{Date Generated}" },
                { label: "Period", value: "{Period}" },
                { label: "Page Number", value: "{Page Number}" },
                { label: "Generated By", value: "{Generated By}" },
              ];

              const insertToken = (
                sectionId: string,
                token: string,
                currentText: string,
              ) => {
                setSections((prev) =>
                  prev.map((s) =>
                    s.id === sectionId
                      ? { ...s, textContent: (currentText ?? "") + token }
                      : s,
                  ),
                );
              };

              return (
                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground">
                    Define the sections of your report and arrange them in
                    order. Sections are optional — all selected metrics are
                    included in the report regardless.
                  </p>

                  {/* Smart suggestions */}
                  {suggestions.length > 0 && (
                    <div className="bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800 rounded-lg p-3">
                      <p className="text-xs font-medium text-teal-700 dark:text-teal-300 mb-2 flex items-center gap-1.5">
                        <Sparkles className="h-3.5 w-3.5" />
                        Suggested sections based on your metrics
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {suggestions.map((s) => (
                          <button
                            key={s.title}
                            type="button"
                            onClick={() =>
                              setSections((prev) => [
                                ...prev,
                                {
                                  id: `section-${Date.now()}-${Math.random()}`,
                                  title: s.title,
                                  type: s.type,
                                  order: prev.length,
                                },
                              ])
                            }
                            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-white dark:bg-teal-900/40 border border-teal-300 dark:border-teal-700 text-xs text-teal-700 dark:text-teal-300 hover:bg-teal-100 dark:hover:bg-teal-800/40 transition-colors"
                          >
                            <Plus className="h-3 w-3" />
                            {s.title}
                            <span className="text-teal-500 capitalize">
                              ({s.type === "kpi" ? "KPI Cards" : s.type})
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Section list + Add Section button — all inside scroll container */}
                  <div className="max-h-[280px] overflow-y-auto pr-1 space-y-2">
                    {sections.length === 0 ? (
                      <div className="border-2 border-dashed border-muted-foreground/20 rounded-lg p-6 text-center">
                        <LayoutTemplate className="h-7 w-7 mx-auto mb-2 text-muted-foreground/40" />
                        <p className="text-sm text-muted-foreground">
                          No sections added yet
                        </p>
                        <p className="text-xs text-muted-foreground/70 mt-0.5">
                          Use a suggestion above or add one manually below
                        </p>
                      </div>
                    ) : (
                      sections.map((section, idx) => {
                        const allMetrics = buildMetricGroups().flatMap(
                          (g) => g.metrics,
                        );
                        const availableMetrics = allMetrics.filter((m) =>
                          selectedMetrics.includes(m.id),
                        );
                        return (
                          <div
                            key={section.id}
                            className="border rounded-lg overflow-hidden"
                          >
                            {/* Section header row */}
                            <div className="flex items-center gap-2 p-2.5 bg-muted/30">
                              {/* Up/down */}
                              <div className="flex flex-col gap-0.5 shrink-0">
                                <button
                                  type="button"
                                  onClick={() => moveSectionUp(idx)}
                                  disabled={idx === 0}
                                  className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                                >
                                  <ChevronUp className="h-3.5 w-3.5" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => moveSectionDown(idx)}
                                  disabled={idx === sections.length - 1}
                                  className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                                >
                                  <ChevronDown className="h-3.5 w-3.5" />
                                </button>
                              </div>
                              {/* Name */}
                              <input
                                type="text"
                                value={section.title}
                                onChange={(e) =>
                                  setSections((prev) =>
                                    prev.map((s) =>
                                      s.id === section.id
                                        ? { ...s, title: e.target.value }
                                        : s,
                                    ),
                                  )
                                }
                                className="flex-1 h-7 rounded-md border border-input bg-background px-2.5 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                              />
                              {/* Type */}
                              <Select
                                value={section.type}
                                onValueChange={(val) =>
                                  setSections((prev) =>
                                    prev.map((s) =>
                                      s.id === section.id
                                        ? {
                                            ...s,
                                            type: val as CustomReportSection["type"],
                                          }
                                        : s,
                                    ),
                                  )
                                }
                              >
                                <SelectTrigger className="w-28 h-7 text-xs shrink-0">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="kpi">KPI Cards</SelectItem>
                                  <SelectItem value="chart">Chart</SelectItem>
                                  <SelectItem value="table">Table</SelectItem>
                                  <SelectItem value="text">
                                    Text Block
                                  </SelectItem>
                                </SelectContent>
                              </Select>
                              {/* Delete */}
                              <button
                                type="button"
                                onClick={() => removeSection(section.id)}
                                className="text-muted-foreground hover:text-destructive transition-colors shrink-0"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </div>

                            {/* KPI Cards config */}
                            {section.type === "kpi" && (
                              <div className="p-3 border-t bg-background space-y-2">
                                <p className="text-xs font-medium text-muted-foreground">
                                  Choose which of your {availableMetrics.length}{" "}
                                  selected metric
                                  {availableMetrics.length !== 1 ? "s" : ""} to
                                  show as KPI cards in this section:
                                </p>
                                {availableMetrics.length === 0 ? (
                                  <p className="text-xs text-muted-foreground/60 italic">
                                    No metrics selected — go back to the Metrics
                                    step to choose some.
                                  </p>
                                ) : (
                                  <div className="grid grid-cols-2 gap-1.5">
                                    {availableMetrics.map((m) => (
                                      <label
                                        key={m.id}
                                        className="flex items-center gap-2 text-xs cursor-pointer hover:text-foreground"
                                      >
                                        <input
                                          type="checkbox"
                                          checked={(
                                            section.kpiMetrics ?? []
                                          ).includes(m.id)}
                                          onChange={() =>
                                            setSections((prev) =>
                                              prev.map((s) => {
                                                if (s.id !== section.id)
                                                  return s;
                                                const current =
                                                  s.kpiMetrics ?? [];
                                                return {
                                                  ...s,
                                                  kpiMetrics: current.includes(
                                                    m.id,
                                                  )
                                                    ? current.filter(
                                                        (x) => x !== m.id,
                                                      )
                                                    : [...current, m.id],
                                                };
                                              }),
                                            )
                                          }
                                          className="rounded"
                                        />
                                        <span className="text-muted-foreground">
                                          {m.label}
                                        </span>
                                      </label>
                                    ))}
                                  </div>
                                )}
                                <p className="text-xs text-muted-foreground/60">
                                  Leave all unchecked to include all selected
                                  metrics.
                                </p>
                              </div>
                            )}

                            {/* Chart config */}
                            {section.type === "chart" && (
                              <div className="p-3 border-t bg-background space-y-3">
                                <div className="space-y-1.5">
                                  <p className="text-xs font-medium text-muted-foreground">
                                    Chart type:
                                  </p>
                                  <div className="flex gap-2">
                                    {(["bar", "line", "area"] as const).map(
                                      (ct) => (
                                        <button
                                          key={ct}
                                          type="button"
                                          onClick={() =>
                                            setSections((prev) =>
                                              prev.map((s) =>
                                                s.id === section.id
                                                  ? { ...s, chartType: ct }
                                                  : s,
                                              ),
                                            )
                                          }
                                          className={`px-3 py-1 rounded text-xs border transition-colors capitalize ${
                                            (section.chartType ?? "bar") === ct
                                              ? "bg-teal-600 text-white border-teal-600"
                                              : "border-input text-muted-foreground hover:border-teal-400"
                                          }`}
                                        >
                                          {ct}
                                        </button>
                                      ),
                                    )}
                                  </div>
                                </div>
                                <div className="space-y-1.5">
                                  <p className="text-xs font-medium text-muted-foreground">
                                    Data series (metrics to plot):
                                  </p>
                                  {availableMetrics.length === 0 ? (
                                    <p className="text-xs text-muted-foreground/60 italic">
                                      No metrics selected — go back to the
                                      Metrics step to choose some.
                                    </p>
                                  ) : (
                                    <div className="grid grid-cols-2 gap-1.5">
                                      {availableMetrics.map((m) => (
                                        <label
                                          key={m.id}
                                          className="flex items-center gap-2 text-xs cursor-pointer"
                                        >
                                          <input
                                            type="checkbox"
                                            checked={(
                                              section.chartMetrics ?? []
                                            ).includes(m.id)}
                                            onChange={() =>
                                              setSections((prev) =>
                                                prev.map((s) => {
                                                  if (s.id !== section.id)
                                                    return s;
                                                  const current =
                                                    s.chartMetrics ?? [];
                                                  return {
                                                    ...s,
                                                    chartMetrics:
                                                      current.includes(m.id)
                                                        ? current.filter(
                                                            (x) => x !== m.id,
                                                          )
                                                        : [...current, m.id],
                                                  };
                                                }),
                                              )
                                            }
                                            className="rounded"
                                          />
                                          <span className="text-muted-foreground">
                                            {m.label}
                                          </span>
                                        </label>
                                      ))}
                                    </div>
                                  )}
                                  <p className="text-xs text-muted-foreground/60">
                                    Leave all unchecked to plot all selected
                                    metrics.
                                  </p>
                                </div>
                              </div>
                            )}

                            {/* Table config */}
                            {section.type === "table" && (
                              <div className="p-3 border-t bg-background space-y-2">
                                <div>
                                  <p className="text-xs font-medium text-muted-foreground mb-0.5">
                                    Comparison columns
                                  </p>
                                  <p className="text-xs text-muted-foreground/60 mb-2">
                                    Your selected metrics appear as rows. Choose
                                    which comparison columns to display.
                                  </p>
                                  <div className="grid grid-cols-2 gap-1.5">
                                    {[
                                      { id: "period", label: "Period / Date" },
                                      {
                                        id: "current",
                                        label: "Current Period",
                                      },
                                      { id: "prior", label: "Prior Period" },
                                      { id: "ytd", label: "Year to Date" },
                                      { id: "budget", label: "Budget" },
                                      {
                                        id: "variance_abs",
                                        label: "Variance (£)",
                                      },
                                      {
                                        id: "variance_pct",
                                        label: "Variance (%)",
                                      },
                                      { id: "trend", label: "Trend" },
                                    ].map((col) => (
                                      <label
                                        key={col.id}
                                        className="flex items-center gap-2 text-xs cursor-pointer"
                                      >
                                        <input
                                          type="checkbox"
                                          checked={(
                                            section.columns ?? [
                                              "period",
                                              "current",
                                              "prior",
                                              "variance_abs",
                                              "variance_pct",
                                            ]
                                          ).includes(col.id)}
                                          onChange={() =>
                                            setSections((prev) =>
                                              prev.map((s) => {
                                                if (s.id !== section.id)
                                                  return s;
                                                const current = s.columns ?? [
                                                  "period",
                                                  "current",
                                                  "prior",
                                                  "variance_abs",
                                                  "variance_pct",
                                                ];
                                                return {
                                                  ...s,
                                                  columns: current.includes(
                                                    col.id,
                                                  )
                                                    ? current.filter(
                                                        (x) => x !== col.id,
                                                      )
                                                    : [...current, col.id],
                                                };
                                              }),
                                            )
                                          }
                                          className="rounded"
                                        />
                                        <span className="text-muted-foreground">
                                          {col.label}
                                        </span>
                                      </label>
                                    ))}
                                  </div>
                                </div>
                                <label className="flex items-center gap-2 text-xs cursor-pointer mt-1">
                                  <input
                                    type="checkbox"
                                    checked={section.showTotals ?? true}
                                    onChange={() =>
                                      setSections((prev) =>
                                        prev.map((s) =>
                                          s.id === section.id
                                            ? {
                                                ...s,
                                                showTotals: !(
                                                  s.showTotals ?? true
                                                ),
                                              }
                                            : s,
                                        ),
                                      )
                                    }
                                    className="rounded"
                                  />
                                  <span className="text-muted-foreground">
                                    Show totals row
                                  </span>
                                </label>
                                <p className="text-xs text-muted-foreground/60">
                                  Leave all unchecked to include all selected
                                  metrics as columns.
                                </p>
                              </div>
                            )}

                            {/* Text block editor */}
                            {section.type === "text" && (
                              <div className="p-3 border-t bg-background space-y-2">
                                <div className="flex flex-wrap gap-1.5 items-center">
                                  <span className="text-xs text-muted-foreground mr-1">
                                    Insert:
                                  </span>
                                  {DYNAMIC_TOKENS.map((token) => (
                                    <button
                                      key={token.value}
                                      type="button"
                                      onClick={() =>
                                        insertToken(
                                          section.id,
                                          token.value,
                                          section.textContent ?? "",
                                        )
                                      }
                                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-teal-100 dark:bg-teal-900/40 text-teal-700 dark:text-teal-300 text-xs hover:bg-teal-200 dark:hover:bg-teal-800/60 transition-colors font-mono"
                                    >
                                      {token.label}
                                    </button>
                                  ))}
                                </div>
                                <textarea
                                  value={section.textContent ?? ""}
                                  onChange={(e) =>
                                    setSections((prev) =>
                                      prev.map((s) =>
                                        s.id === section.id
                                          ? {
                                              ...s,
                                              textContent: e.target.value,
                                            }
                                          : s,
                                      ),
                                    )
                                  }
                                  rows={3}
                                  placeholder="Enter your text here. Use the Insert buttons above to add dynamic values that update automatically when the report is generated."
                                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring resize-none font-mono"
                                />
                                <p className="text-xs text-muted-foreground/70">
                                  Dynamic values like{" "}
                                  <span className="font-mono text-teal-600">
                                    {"{Company Name}"}
                                  </span>{" "}
                                  are resolved automatically at report
                                  generation time.
                                </p>
                              </div>
                            )}
                          </div>
                        );
                      })
                    )}
                    {/* Add Section — inside scroll container, same width as section rows */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={addSection}
                      className="w-full border-dashed"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Section
                    </Button>
                  </div>
                </div>
              );
            })()}

          {/* Step: Branding */}
          {step === "branding" && (
            <div className="space-y-6">
              {/* Colour preset */}
              <div className="space-y-3">
                <Label className="font-semibold">Colour Scheme</Label>
                <div className="grid grid-cols-2 gap-3">
                  {BRANDING_PRESETS.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setBrandingPreset(p.id)}
                      className={`flex items-center gap-3 p-3 border rounded-lg transition-colors text-left ${
                        brandingPreset === p.id
                          ? "border-teal-500 bg-teal-50 dark:bg-teal-900/20"
                          : "hover:bg-muted/50"
                      }`}
                    >
                      <div className="flex gap-1">
                        <div
                          className="h-6 w-6 rounded-full border"
                          style={{ backgroundColor: p.primaryColour }}
                        />
                        <div
                          className="h-6 w-6 rounded-full border"
                          style={{ backgroundColor: p.accentColour }}
                        />
                      </div>
                      <div>
                        <p className="text-sm font-medium">{p.name}</p>
                        <p className="text-xs text-muted-foreground capitalize">
                          {p.headerStyle} header
                        </p>
                      </div>
                      {brandingPreset === p.id && (
                        <CheckCircle2 className="h-4 w-4 text-teal-600 ml-auto" />
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Logo */}
              <div className="space-y-3">
                <Label className="font-semibold">Logo</Label>
                <div className="border-2 border-dashed border-muted-foreground/20 rounded-lg p-6 text-center">
                  <ImageIcon className="h-8 w-8 mx-auto mb-2 text-muted-foreground/40" />
                  <p className="text-sm text-muted-foreground mb-3">
                    Upload a logo for this report
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      toast.info(
                        "Logo upload — stored in memory for this session. In live mode logos are saved to your organisation's logo library.",
                      )
                    }
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Upload Logo
                  </Button>
                  <p className="text-xs text-muted-foreground mt-3">
                    PNG or SVG recommended. Logos are stored in the organisation
                    logo library and can be reused across reports.
                  </p>
                </div>
              </div>

              {/* Header template */}
              <div className="space-y-3">
                <Label className="font-semibold">Header Template</Label>
                <div className="space-y-2">
                  <label className="flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-muted/50">
                    <input
                      type="radio"
                      checked={headerTemplate === null}
                      onChange={() => setHeaderTemplate(null)}
                      className="h-4 w-4"
                    />
                    <span className="text-sm">No header</span>
                  </label>
                  {HEADER_TEMPLATES.map((t) => (
                    <label
                      key={t.id}
                      className="flex items-start gap-2 p-3 border rounded-lg cursor-pointer hover:bg-muted/50 transition-colors"
                    >
                      <input
                        type="radio"
                        checked={headerTemplate === t.id}
                        onChange={() => setHeaderTemplate(t.id)}
                        className="h-4 w-4 mt-0.5"
                      />
                      <div>
                        <p className="text-sm font-medium">{t.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {t.content}
                        </p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Footer template */}
              <div className="space-y-3">
                <Label className="font-semibold">Footer Template</Label>
                <div className="space-y-2">
                  <label className="flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-muted/50">
                    <input
                      type="radio"
                      checked={footerTemplate === null}
                      onChange={() => setFooterTemplate(null)}
                      className="h-4 w-4"
                    />
                    <span className="text-sm">No footer</span>
                  </label>
                  {FOOTER_TEMPLATES.map((t) => (
                    <label
                      key={t.id}
                      className="flex items-start gap-2 p-3 border rounded-lg cursor-pointer hover:bg-muted/50 transition-colors"
                    >
                      <input
                        type="radio"
                        checked={footerTemplate === t.id}
                        onChange={() => setFooterTemplate(t.id)}
                        className="h-4 w-4 mt-0.5"
                      />
                      <div>
                        <p className="text-sm font-medium">{t.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {t.content}
                        </p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step: Preview */}
          {step === "preview" && (
            <div className="space-y-5">
              <div className="bg-muted/30 rounded-lg p-5 space-y-4">
                {/* Simulated report header */}
                {preset && (
                  <div
                    className="rounded-lg p-4 text-white"
                    style={{ backgroundColor: preset.primaryColour }}
                  >
                    <p className="font-bold text-lg">
                      {name || "Untitled Report"}
                    </p>
                    <p className="text-sm opacity-80">
                      {description || "No description"}
                    </p>
                    <p className="text-xs opacity-60 mt-1">
                      BuildRight Construction Ltd · Generated{" "}
                      {new Date().toLocaleDateString("en-GB")}
                    </p>
                  </div>
                )}

                {/* Sections preview */}
                {sections.length > 0 ? (
                  <div className="space-y-3">
                    {sections.map((section) => (
                      <div
                        key={section.id}
                        className="border rounded-lg p-4 bg-background"
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <div
                            className="h-1 w-6 rounded"
                            style={{
                              backgroundColor:
                                preset?.primaryColour ?? "#0d9488",
                            }}
                          />
                          <p className="text-sm font-semibold">
                            {section.title}
                          </p>
                          <Badge
                            variant="secondary"
                            className="text-xs ml-auto capitalize"
                          >
                            {section.type}
                          </Badge>
                        </div>
                        <div className="h-12 bg-muted/50 rounded flex items-center justify-center">
                          <p className="text-xs text-muted-foreground">
                            {section.type === "kpi" &&
                              "KPI cards will render here"}
                            {section.type === "chart" &&
                              "Chart will render here"}
                            {section.type === "table" &&
                              "Data table will render here"}
                            {section.type === "text" &&
                              "Text content will render here"}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="border rounded-lg p-6 bg-background text-center">
                    <p className="text-sm text-muted-foreground">
                      No sections defined — go back to Layout to add sections
                    </p>
                  </div>
                )}

                {/* Metrics summary */}
                {selectedMetrics.length > 0 && (
                  <div className="border rounded-lg p-4 bg-background">
                    <p className="text-xs font-semibold text-muted-foreground mb-2">
                      SELECTED METRICS ({selectedMetrics.length})
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {selectedMetrics.map((mId) => {
                        const label =
                          metricGroups
                            .flatMap((g) => g.metrics)
                            .find((m) => m.id === mId)?.label ?? mId;
                        return (
                          <span
                            key={mId}
                            className="text-xs bg-muted rounded-full px-2 py-0.5 text-muted-foreground"
                          >
                            {label}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Output summary */}
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div className="border rounded-lg p-3 bg-background">
                    <p className="text-xs text-muted-foreground">Formats</p>
                    <p className="text-sm font-medium">
                      {selectedFormats.join(", ")}
                    </p>
                  </div>
                  <div className="border rounded-lg p-3 bg-background">
                    <p className="text-xs text-muted-foreground">Branding</p>
                    <p className="text-sm font-medium">
                      {preset?.name ?? "Default"}
                    </p>
                  </div>
                  <div className="border rounded-lg p-3 bg-background">
                    <p className="text-xs text-muted-foreground">Status</p>
                    <p className="text-sm font-medium">Draft</p>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-900 rounded-lg p-4">
                <div className="flex gap-3">
                  <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-blue-900 dark:text-blue-100">
                    Saving will add this report to your{" "}
                    <strong>Custom Reports</strong> workspace as a draft. It
                    won't appear in Available Reports until you choose to
                    publish it and assign role access.
                  </p>
                </div>
              </div>
            </div>
          )}
        </ScrollArea>

        {/* Footer navigation */}
        <div className="px-6 py-4 border-t flex items-center justify-between">
          <Button
            variant="outline"
            onClick={() => {
              if (currentStepIndex === 0) {
                onOpenChange(false);
              } else {
                setStep(steps[currentStepIndex - 1].id);
              }
            }}
          >
            {currentStepIndex === 0 ? "Cancel" : "Back"}
          </Button>

          <div className="flex items-center gap-2">
            {step !== "preview" && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setStep("preview")}
                className="text-muted-foreground"
              >
                Preview
              </Button>
            )}
            {step === "preview" ? (
              <Button
                onClick={handleSave}
                disabled={!name.trim()}
                className="bg-teal-600 hover:bg-teal-700"
              >
                <CheckCircle2 className="h-4 w-4 mr-2" />
                {editingReport ? "Save Changes" : "Save to Custom Reports"}
              </Button>
            ) : (
              <Button
                onClick={() => setStep(steps[currentStepIndex + 1].id)}
                disabled={step === "name" && !name.trim()}
                className="bg-teal-600 hover:bg-teal-700"
              >
                Next
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ─── Reports Grid Row ─────────────────────────────────────────────────────────

interface ScheduleInfo {
  frequency: string;
  day?: string;
  time?: string;
  delivery: string; // e.g. 'Email (PDF + Excel)', 'PDF', 'SharePoint'
  recipients: string;
}

interface ReportGridRowProps {
  name: string;
  description: string;
  category: string;
  formats: string[];
  scheduleInfo?: ScheduleInfo; // Available Reports only — shows schedule indicator
  isProtected?: boolean;
  isFavourited?: boolean;
  isPublished?: boolean;
  onGenerate?: () => void;
  onFavourite?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onPublish?: () => void;
  onClone?: () => void;
  showManageActions?: boolean;
}

const CATEGORY_LABELS: Record<string, string> = {
  financial: "Financial",
  admin: "Administration",
};

const getCategoryLabel = (category: string): string => {
  if (category === "forecast") return getForecastTerm(true);
  if (category === "variance") return `${getVarianceTerm(true)} Analysis`;
  return CATEGORY_LABELS[category] ?? category;
};

const ReportGridRow: React.FC<ReportGridRowProps> = ({
  name,
  description,
  category,
  formats,
  scheduleInfo,
  isProtected,
  isFavourited,
  isPublished,
  onGenerate,
  onFavourite,
  onEdit,
  onDelete,
  onPublish,
  onClone,
  showManageActions,
}) => {
  const DAY_LABELS: Record<string, string> = {
    mon: "Monday",
    tue: "Tuesday",
    wed: "Wednesday",
    thu: "Thursday",
    fri: "Friday",
    sat: "Saturday",
    sun: "Sunday",
  };

  const isAvailableRow = !!onFavourite; // Available Reports rows always have onFavourite
  const isCustomRow = !onFavourite && !!onPublish; // Custom Reports rows have onPublish but no onFavourite

  return (
    <div className="flex items-center gap-4 px-4 py-3 border rounded-lg hover:bg-muted/30 transition-colors group">
      {/* Favourite star — Available Reports tab only */}
      {isAvailableRow && (
        <button
          type="button"
          onClick={onFavourite}
          className={`flex-shrink-0 transition-colors ${
            isFavourited
              ? "text-amber-400"
              : "text-muted-foreground/30 hover:text-amber-400"
          }`}
          title={isFavourited ? "Remove from favourites" : "Add to favourites"}
        >
          <Star className={`h-4 w-4 ${isFavourited ? "fill-amber-400" : ""}`} />
        </button>
      )}

      {/* Publish indicator — Custom Reports tab only */}
      {isCustomRow && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={onPublish}
                className={`flex-shrink-0 transition-colors ${
                  isPublished
                    ? "text-emerald-500"
                    : "text-muted-foreground/30 hover:text-emerald-500"
                }`}
              >
                <CheckCircle2
                  className={`h-4 w-4 ${isPublished ? "fill-emerald-100" : ""}`}
                />
              </button>
            </TooltipTrigger>
            <TooltipContent>
              {isPublished
                ? "Published to Available Reports — click to manage"
                : "Add to Available Reports"}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}

      {/* Report info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm font-semibold truncate">{name}</p>
          {isProtected && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <Lock className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  System template — cloneable but cannot be edited or deleted
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {isPublished !== undefined && !isAvailableRow && (
            <Badge
              variant={isPublished ? "default" : "secondary"}
              className={`text-xs ${isPublished ? "bg-teal-600" : ""}`}
            >
              {isPublished ? "Published" : "Draft"}
            </Badge>
          )}
        </div>
        <p className="text-xs text-muted-foreground truncate mt-0.5">
          {description}
        </p>
      </div>

      {/* Category badge */}
      <div className="hidden md:flex items-center gap-2 flex-shrink-0">
        <Badge variant="secondary" className="text-xs whitespace-nowrap">
          {getCategoryLabel(category)}
        </Badge>
      </div>

      {/* Schedule indicator — Available Reports only, only if scheduled */}
      {isAvailableRow && scheduleInfo && (
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="flex-shrink-0 text-teal-600 hover:text-teal-700 transition-colors"
              title="Scheduled report — click for details"
            >
              <CalendarClock className="h-4 w-4" />
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-64 p-4 space-y-3">
            <div className="flex items-center gap-2">
              <CalendarClock className="h-4 w-4 text-teal-600" />
              <p className="text-sm font-semibold">Schedule</p>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex items-start gap-2">
                <Clock className="h-3.5 w-3.5 text-muted-foreground mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium text-xs text-muted-foreground uppercase tracking-wide mb-0.5">
                    Frequency
                  </p>
                  <p>{scheduleInfo.frequency}</p>
                  {scheduleInfo.day && (
                    <p className="text-muted-foreground text-xs">
                      {DAY_LABELS[scheduleInfo.day] ?? scheduleInfo.day}
                      {scheduleInfo.time ? ` at ${scheduleInfo.time}` : ""}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Mail className="h-3.5 w-3.5 text-muted-foreground mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium text-xs text-muted-foreground uppercase tracking-wide mb-0.5">
                    Delivery
                  </p>
                  <p>{scheduleInfo.delivery}</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <MapPin className="h-3.5 w-3.5 text-muted-foreground mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium text-xs text-muted-foreground uppercase tracking-wide mb-0.5">
                    Recipients
                  </p>
                  <p>{scheduleInfo.recipients}</p>
                </div>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      )}

      {/* Actions */}
      <div className="flex items-center gap-1.5 flex-shrink-0">
        {/* Generate — Available Reports only */}
        {onGenerate && isAvailableRow && (
          <Button
            size="sm"
            variant="outline"
            onClick={onGenerate}
            title="Generate report"
          >
            <FileText className="h-3.5 w-3.5" />
            <span className="hidden sm:inline ml-1.5">Generate</span>
          </Button>
        )}

        {/* Custom Reports actions — Edit, Duplicate, Delete only. No Generate. */}
        {showManageActions && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="ghost" className="h-8 w-8 p-0">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              {onPublish && (
                <DropdownMenuItem onClick={onPublish}>
                  <Share2 className="h-4 w-4 mr-2" />
                  {isPublished
                    ? "Manage Availability"
                    : "Add to Available Reports"}
                </DropdownMenuItem>
              )}
              {!isProtected && onEdit && (
                <DropdownMenuItem onClick={onEdit}>
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </DropdownMenuItem>
              )}
              {onClone && (
                <DropdownMenuItem onClick={onClone}>
                  <Copy className="h-4 w-4 mr-2" />
                  {isProtected ? "Clone to Custom" : "Duplicate"}
                </DropdownMenuItem>
              )}
              {!isProtected && onDelete && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={onDelete}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  );
};

// ─── Search + Filter Bar ──────────────────────────────────────────────────────

interface SearchFilterBarProps {
  search: string;
  onSearchChange: (v: string) => void;
  categoryFilter: string;
  onCategoryChange: (v: string) => void;
  formatFilter: string;
  onFormatChange: (v: string) => void;
  showAdminCategories?: boolean;
}

const SearchFilterBar: React.FC<SearchFilterBarProps> = ({
  search,
  onSearchChange,
  categoryFilter,
  onCategoryChange,
  formatFilter,
  onFormatChange,
  showAdminCategories,
}) => (
  <div className="flex flex-col sm:flex-row gap-3">
    <div className="relative flex-1">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
      <Input
        value={search}
        onChange={(e) => onSearchChange(e.target.value)}
        placeholder="Search reports..."
        className="pl-9"
      />
      {search && (
        <button
          type="button"
          onClick={() => onSearchChange("")}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
    <Select value={categoryFilter} onValueChange={onCategoryChange}>
      <SelectTrigger className="w-full sm:w-44">
        <Filter className="h-4 w-4 mr-2 text-muted-foreground" />
        <SelectValue placeholder="Category" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">All Categories</SelectItem>
        {showAdminCategories && (
          <SelectItem value="admin">Administration</SelectItem>
        )}
        <SelectItem value="financial">Financial</SelectItem>
        <SelectItem value="forecast">{getForecastTerm(true)}</SelectItem>
        <SelectItem value="variance">
          {getVarianceTerm(true)} Analysis
        </SelectItem>
      </SelectContent>
    </Select>
    <Select value={formatFilter} onValueChange={onFormatChange}>
      <SelectTrigger className="w-full sm:w-36">
        <SelectValue placeholder="Format" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">All Formats</SelectItem>
        <SelectItem value="PDF">PDF</SelectItem>
        <SelectItem value="Excel">Excel</SelectItem>
        <SelectItem value="CSV">CSV</SelectItem>
        <SelectItem value="Email">Email</SelectItem>
      </SelectContent>
    </Select>
  </div>
);

// ─── Role Access Control ──────────────────────────────────────────────────────

interface RoleAccessEntry {
  reportId: string;
  reportName: string;
  isCustom: boolean;
  roleIds: string[];
}

interface RoleAccessControlProps {
  entries: RoleAccessEntry[];
  onUpdate: (reportId: string, roleIds: string[]) => void;
}

const RoleAccessControl: React.FC<RoleAccessControlProps> = ({
  entries,
  onUpdate,
}) => {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");

  const filtered = entries.filter((e) => {
    const matchSearch =
      !search || e.reportName.toLowerCase().includes(search.toLowerCase());
    const matchRole = roleFilter === "all" || e.roleIds.includes(roleFilter);
    return matchSearch && matchRole;
  });

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search reports..."
            className="pl-9"
          />
        </div>
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="w-full sm:w-52">
            <SelectValue placeholder="Filter by role" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Roles</SelectItem>
            {DEMO_ROLES.map((r) => (
              <SelectItem key={r.id} value={r.id}>
                {r.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground text-sm">
          No reports match your search
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((entry) => (
            <div
              key={entry.reportId}
              className="border rounded-lg overflow-hidden"
            >
              <button
                type="button"
                onClick={() =>
                  setExpandedId(
                    expandedId === entry.reportId ? null : entry.reportId,
                  )
                }
                className="w-full flex items-center gap-3 p-4 text-left hover:bg-muted/30 transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium truncate">
                      {entry.reportName}
                    </p>
                    <Badge
                      variant={entry.isCustom ? "secondary" : "outline"}
                      className="text-xs flex-shrink-0"
                    >
                      {entry.isCustom ? "Custom" : "Template"}
                    </Badge>
                  </div>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {entry.roleIds.length === 0 ? (
                      <span className="text-xs text-muted-foreground italic">
                        No roles assigned
                      </span>
                    ) : (
                      entry.roleIds.map((roleId) => {
                        const role = DEMO_ROLES.find((r) => r.id === roleId);
                        return (
                          <span
                            key={roleId}
                            className="text-xs bg-teal-50 text-teal-700 border border-teal-200 rounded-full px-2 py-0.5 dark:bg-teal-900/20 dark:text-teal-400 dark:border-teal-800"
                          >
                            {role?.name ?? roleId}
                          </span>
                        );
                      })
                    )}
                  </div>
                </div>
                {expandedId === entry.reportId ? (
                  <ChevronUp className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                )}
              </button>

              {expandedId === entry.reportId && (
                <div className="border-t px-4 py-3 bg-muted/20">
                  <p className="text-xs font-medium text-muted-foreground mb-3">
                    Toggle role access for this report:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {DEMO_ROLES.map((role) => {
                      const active = entry.roleIds.includes(role.id);
                      return (
                        <button
                          key={role.id}
                          type="button"
                          onClick={() => {
                            const updated = active
                              ? entry.roleIds.filter((r) => r !== role.id)
                              : [...entry.roleIds, role.id];
                            onUpdate(entry.reportId, updated);
                          }}
                          className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                            active
                              ? "bg-teal-600 text-white border-teal-600"
                              : "text-muted-foreground border-muted-foreground/30 hover:border-teal-400 hover:text-teal-600"
                          }`}
                        >
                          {role.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Page component ───────────────────────────────────────────────────────────

export const ReportsPage: React.FC = () => {
  const { formatDate } = useFormatting();
  const { hasPermission } = usePermissions();
  const { currentUser } = useApp();
  const {
    getForecastTerm,
    getCashMovementTerms,
    getVarianceTerm,
    getScenarioTerm,
    getCashDurationShortTerm,
    getCategoryLabel,
  } = useTerminology();

  // ── Permissions ──
  const canViewReports = hasPermission("view_reports");
  const canManageCustomReports = hasPermission("manage_custom_reports");
  const isSystemAdmin = hasPermission("manage_roles_permissions");

  // ── Template reports — built once ──
  const templateReports = useMemo(() => buildTemplateReports(), []);
  const adminTemplateReports = useMemo(() => buildAdminTemplateReports(), []);

  // ── Visible template reports based on user's role ──
  // In live mode this would be filtered by the user's actual role.
  // In demo mode we resolve via the role template ID from the user's forecasterRole.
  // Map some role template IDs to report access groups
  const ROLE_TO_REPORT_GROUP: Record<string, string> = {
    preparer: "preparer",
    manager: "manager",
    regional: "manager",
    cfo: "cfo",
    executive: "ceo",
    external: "external",
    system_admin: "admin",
  };
  const roleTemplateId = getRoleTemplateId(currentUser.forecasterRole);
  const currentRoleId = ROLE_TO_REPORT_GROUP[roleTemplateId] ?? "manager";

  const visibleTemplates = isSystemAdmin
    ? adminTemplateReports
    : templateReports.filter((r) => r.defaultRoleIds.includes(currentRoleId));

  // ── State ──
  const [activeReport, setActiveReport] = useState<ReportDefinition | null>(
    null,
  );
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [deleteScheduleConfirm, setDeleteScheduleConfirm] = useState<
    string | null
  >(null);
  const [editingSchedule, setEditingSchedule] =
    useState<ScheduledReport | null>(null);
  const [scheduledReports, setScheduledReports] = useState<ScheduledReport[]>(
    buildScheduledReports,
  );
  const [reportHistory] = useState(buildReportHistory);
  // Admin persona sees admin-scoped custom reports; all others see finance reports
  const initialCustomReports = isSystemAdmin
    ? buildAdminDemoCustomReports()
    : buildDemoCustomReports();
  const [customReports, setCustomReports] =
    useState<CustomReport[]>(initialCustomReports);
  const [favouritedIds, setFavouritedIds] = useState<string[]>([]);
  const [favouritedTemplateIds, setFavouritedTemplateIds] = useState<string[]>(
    [],
  );

  // Manage tab role access state — includes all reports
  const [roleAccessEntries, setRoleAccessEntries] = useState<RoleAccessEntry[]>(
    () => [
      ...templateReports.map((r) => ({
        reportId: r.id,
        reportName: r.name,
        isCustom: false,
        roleIds: r.defaultRoleIds,
      })),
    ],
  );

  // Builder wizard state
  const [builderOpen, setBuilderOpen] = useState(false);
  const [editingCustomReport, setEditingCustomReport] =
    useState<CustomReport | null>(null);
  const [cloneSource, setCloneSource] = useState<ReportDefinition | null>(null);

  // Publish dialog state
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [publishTarget, setPublishTarget] = useState<CustomReport | null>(null);

  // Clone picker dialog state (searchable modal)
  const [clonePickerOpen, setClonePickerOpen] = useState(false);

  // Clone template dialog state (name/description step after picking)
  const [cloneDialogOpen, setCloneDialogOpen] = useState(false);
  const [pendingCloneAction, setPendingCloneAction] = useState<"save" | "edit">(
    "save",
  );
  const [cloneTemplate, setCloneTemplate] = useState<ReportDefinition | null>(
    null,
  );

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<CustomReport | null>(null);

  // Available Reports tab state
  const [availSearch, setAvailSearch] = useState("");
  const [availCategory, setAvailCategory] = useState("all");
  const [availFormat, setAvailFormat] = useState("all");

  // Custom Reports tab state
  const [customSearch, setCustomSearch] = useState("");
  const [customCategory, setCustomCategory] = useState("all");
  const [customFormat, setCustomFormat] = useState("all");
  const [customStatusFilter, setCustomStatusFilter] = useState<
    "all" | "draft" | "published"
  >("all");

  // ── Derived: all reports visible in Available Reports ──
  const publishedCustomReportsForRole = customReports.filter(
    (r) => r.isPublished && r.publishedToRoleIds.includes(currentRoleId),
  );

  // All reports visible in Available Reports tab
  const allAvailableReports: (ReportDefinition & {
    isFavourited?: boolean;
    isCustom?: boolean;
  })[] = [
    ...visibleTemplates.map((r) => ({
      ...r,
      isFavourited: favouritedTemplateIds.includes(r.id),
    })),
    ...publishedCustomReportsForRole.map((r) => ({
      id: r.id,
      name: r.name,
      description: r.description,
      category: "custom",
      formats: r.formats,
      isFavourited: favouritedIds.includes(r.id),
      isCustom: true,
    })),
  ];

  // Favourited reports for the strip
  const favouritedReports = allAvailableReports.filter((r) => r.isFavourited);

  // Filtered available reports (excluding favourites from main grid — they're shown in strip)
  const filteredAvailable = allAvailableReports.filter((r) => {
    const matchSearch =
      !availSearch ||
      r.name.toLowerCase().includes(availSearch.toLowerCase()) ||
      (r.description ?? "").toLowerCase().includes(availSearch.toLowerCase());
    const matchCategory =
      availCategory === "all" || r.category === availCategory;
    const matchFormat =
      availFormat === "all" || r.formats.includes(availFormat);
    return matchSearch && matchCategory && matchFormat;
  });

  // ── Derived: filtered custom reports ──
  const filteredCustomReports = customReports.filter((r) => {
    const matchSearch =
      !customSearch ||
      r.name.toLowerCase().includes(customSearch.toLowerCase()) ||
      r.description.toLowerCase().includes(customSearch.toLowerCase());
    const matchStatus =
      customStatusFilter === "all" || r.status === customStatusFilter;
    const matchFormat =
      customFormat === "all" || r.formats.includes(customFormat);
    return matchSearch && matchStatus && matchFormat;
  });

  // ── Handlers ──

  const handleSaveSchedule = (schedule: Omit<ScheduledReport, "id">) => {
    if (editingSchedule) {
      setScheduledReports((prev) =>
        prev.map((s) =>
          s.id === editingSchedule.id
            ? { ...schedule, id: editingSchedule.id }
            : s,
        ),
      );
    } else {
      setScheduledReports((prev) => [
        ...prev,
        { ...schedule, id: `sched-${Date.now()}` },
      ]);
    }
    setEditingSchedule(null);
    toast.success("Schedule saved");
  };

  const handleTogglePause = (scheduleId: string) => {
    setScheduledReports((prev) =>
      prev.map((s) =>
        s.id === scheduleId
          ? { ...s, status: s.status === "active" ? "paused" : "active" }
          : s,
      ),
    );
  };

  const handleDeleteSchedule = (scheduleId: string) => {
    setScheduledReports((prev) => prev.filter((s) => s.id !== scheduleId));
    toast.success("Schedule removed");
  };

  const handleToggleFavourite = (reportId: string, isTemplate: boolean) => {
    if (isTemplate) {
      setFavouritedTemplateIds((prev) =>
        prev.includes(reportId)
          ? prev.filter((id) => id !== reportId)
          : [...prev, reportId],
      );
    } else {
      setFavouritedIds((prev) =>
        prev.includes(reportId)
          ? prev.filter((id) => id !== reportId)
          : [...prev, reportId],
      );
      setCustomReports((prev) =>
        prev.map((r) =>
          r.id === reportId ? { ...r, isFavourited: !r.isFavourited } : r,
        ),
      );
    }
  };

  const handleSaveCustomReport = (
    data: Omit<
      CustomReport,
      "id" | "createdAt" | "updatedAt" | "createdBy" | "createdByName"
    >,
  ) => {
    if (editingCustomReport) {
      setCustomReports((prev) =>
        prev.map((r) =>
          r.id === editingCustomReport.id
            ? { ...r, ...data, updatedAt: new Date() }
            : r,
        ),
      );
      toast.success("Report updated");
    } else {
      const newReport: CustomReport = {
        ...data,
        id: `custom-${Date.now()}`,
        createdBy: String(currentUser.id),
        createdByName: currentUser.name,
        createdAt: new Date(),
        updatedAt: new Date(),
      };
      setCustomReports((prev) => [...prev, newReport]);
      toast.success("Report saved to Custom Reports");
    }
    setEditingCustomReport(null);
    setCloneSource(null);
  };

  const handlePublish = (reportId: string, roleIds: string[]) => {
    setCustomReports((prev) =>
      prev.map((r) =>
        r.id === reportId
          ? {
              ...r,
              isPublished: true,
              publishedToRoleIds: roleIds,
              status: "published",
            }
          : r,
      ),
    );
    // Add to role access entries for Manage tab
    const report = customReports.find((r) => r.id === reportId);
    if (report) {
      setRoleAccessEntries((prev) => {
        const exists = prev.find((e) => e.reportId === reportId);
        if (exists) {
          return prev.map((e) =>
            e.reportId === reportId ? { ...e, roleIds } : e,
          );
        }
        return [
          ...prev,
          { reportId, reportName: report.name, isCustom: true, roleIds },
        ];
      });
    }
    toast.success("Report published to Available Reports");
  };

  const handleUnpublish = (reportId: string) => {
    setCustomReports((prev) =>
      prev.map((r) =>
        r.id === reportId
          ? {
              ...r,
              isPublished: false,
              publishedToRoleIds: [],
              status: "draft",
            }
          : r,
      ),
    );
    setRoleAccessEntries((prev) => prev.filter((e) => e.reportId !== reportId));
    toast.success("Report removed from Available Reports");
  };

  const handleDeleteCustomReport = (report: CustomReport) => {
    setDeleteTarget(report);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (!deleteTarget) return;
    setCustomReports((prev) => prev.filter((r) => r.id !== deleteTarget.id));
    setRoleAccessEntries((prev) =>
      prev.filter((e) => e.reportId !== deleteTarget.id),
    );
    toast.success(`"${deleteTarget.name}" deleted`);
    setDeleteTarget(null);
  };

  const handleClonePickerSelect = (
    report: ReportDefinition | CustomReport,
    type: "template" | "custom",
    action: "save" | "edit",
  ) => {
    if (type === "template") {
      // Templates always go through the name dialog first, then honour the action
      setCloneTemplate(report as ReportDefinition);
      setCloneDialogOpen(true);
      // Store the intended action so the name dialog can route correctly after naming
      setPendingCloneAction(action);
    } else {
      // Clone a custom report
      const source = report as CustomReport;
      const newReport: CustomReport = {
        ...source,
        id: `custom-${Date.now()}`,
        name: `Copy of ${source.name}`,
        createdAt: new Date(),
        updatedAt: new Date(),
        isPublished: false,
        publishedToRoleIds: [],
        isFavourited: false,
        status: "draft",
      };
      setCustomReports((prev) => [...prev, newReport]);
      if (action === "edit") {
        // Open in editor — load the new report into the builder
        setEditingCustomReport(newReport);
        setBuilderOpen(true);
        toast.success(`"Copy of ${source.name}" created — opening in editor`);
      } else {
        toast.success(`"Copy of ${source.name}" saved to your workspace`);
      }
    }
  };

  const handleCloneTemplate = (
    template: ReportDefinition,
    name: string,
    description: string,
  ) => {
    const newReport: CustomReport = {
      id: `custom-${Date.now()}`,
      name,
      description,
      createdBy: String(currentUser.id),
      createdByName: currentUser.name,
      createdAt: new Date(),
      updatedAt: new Date(),
      isPublished: false,
      publishedToRoleIds: [],
      isFavourited: false,
      metrics: [],
      sections: [],
      brandingPreset: "default",
      logoId: null,
      headerTemplate: "h1",
      footerTemplate: "f1",
      status: "draft",
      formats: template.formats,
    };
    setCustomReports((prev) => [...prev, newReport]);
    if (pendingCloneAction === "edit") {
      setEditingCustomReport(newReport);
      setBuilderOpen(true);
      toast.success(`"${name}" created — opening in editor`);
    } else {
      toast.success(`"${name}" saved to your workspace`);
    }
    setPendingCloneAction("save"); // reset
  };

  const handleRoleAccessUpdate = (reportId: string, roleIds: string[]) => {
    setRoleAccessEntries((prev) =>
      prev.map((e) => (e.reportId === reportId ? { ...e, roleIds } : e)),
    );
  };

  const deleteTargetLinkedRoles =
    deleteTarget?.publishedToRoleIds
      .map((id) => DEMO_ROLES.find((r) => r.id === id)?.name ?? id)
      .filter(Boolean) ?? [];

  // All schedulable reports (templates + published custom)
  const allSchedulableReports: ReportDefinition[] = [
    ...visibleTemplates,
    ...publishedCustomReportsForRole.map((r) => ({
      id: r.id,
      name: r.name,
      description: r.description,
      category: "custom",
      formats: r.formats,
      schedulable: true,
    })),
  ];

  const defaultTab = canViewReports ? "available" : "manage";

  // ── Stats ──
  const activeSchedules = scheduledReports.filter(
    (s) => s.status === "active",
  ).length;
  const generatedToday = reportHistory.filter(
    (h) => h.date.toDateString() === new Date().toDateString(),
  ).length;
  const draftCustomCount = customReports.filter(
    (r) => r.status === "draft",
  ).length;

  return (
    <div className="p-6 space-y-6">
      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">
                  Available Reports
                </p>
                <p className="text-2xl font-bold">
                  {allAvailableReports.length}
                </p>
              </div>
              <FileText className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
        {canManageCustomReports && (
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">
                    Custom Reports
                  </p>
                  <p className="text-2xl font-bold">{customReports.length}</p>
                  {draftCustomCount > 0 && (
                    <p className="text-xs text-muted-foreground">
                      {draftCustomCount} draft
                    </p>
                  )}
                </div>
                <LayoutTemplate className="h-8 w-8 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
        )}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">
                  Active Schedules
                </p>
                <p className="text-2xl font-bold">{activeSchedules}</p>
              </div>
              <Clock className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Generated Today</p>
                <p className="text-2xl font-bold">{generatedToday}</p>
              </div>
              <Download className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main tabs */}
      <Tabs defaultValue={defaultTab} className="space-y-6">
        <TabsList>
          {canViewReports && (
            <TabsTrigger value="available">Available Reports</TabsTrigger>
          )}
          {canManageCustomReports && (
            <TabsTrigger value="custom">Custom Reports</TabsTrigger>
          )}
          {canViewReports && (
            <TabsTrigger value="scheduled">Scheduled</TabsTrigger>
          )}
          {canViewReports && <TabsTrigger value="history">History</TabsTrigger>}
          {canManageCustomReports && (
            <TabsTrigger value="manage" className="flex items-center gap-1.5">
              <Settings className="h-3.5 w-3.5" />
              Manage
            </TabsTrigger>
          )}
        </TabsList>

        {/* ── Available Reports ── */}
        {canViewReports && (
          <TabsContent value="available" className="space-y-5">
            {/* Favourites strip */}
            {favouritedReports.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Star className="h-4 w-4 text-amber-400 fill-amber-400" />
                  <h3 className="text-sm font-semibold">Favourites</h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {favouritedReports.map((report) => {
                    const isTemplate = !(
                      "isCustom" in report && report.isCustom
                    );
                    return (
                      <Card
                        key={`fav-${report.id}`}
                        className="hover:shadow-md transition-shadow cursor-pointer group"
                        onClick={() => setActiveReport(report)}
                      >
                        <CardContent className="pt-4 pb-4">
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-semibold truncate">
                                {report.name}
                              </p>
                              <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                                {report.description}
                              </p>
                            </div>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleToggleFavourite(report.id, isTemplate);
                              }}
                              className="text-amber-400 flex-shrink-0"
                            >
                              <Star className="h-4 w-4 fill-amber-400" />
                            </button>
                          </div>
                          <div className="flex items-center gap-2 mt-3 flex-wrap">
                            {report.formats.slice(0, 3).map((f) => (
                              <span
                                key={f}
                                className="text-xs border rounded px-1.5 py-0.5 text-muted-foreground"
                              >
                                {f}
                              </span>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Search + filters */}
            <SearchFilterBar
              search={availSearch}
              onSearchChange={setAvailSearch}
              categoryFilter={availCategory}
              onCategoryChange={setAvailCategory}
              formatFilter={availFormat}
              onFormatChange={setAvailFormat}
              showAdminCategories={isSystemAdmin}
            />

            {/* Results count */}
            {(availSearch ||
              availCategory !== "all" ||
              availFormat !== "all") && (
              <p className="text-sm text-muted-foreground">
                {filteredAvailable.length} report
                {filteredAvailable.length !== 1 ? "s" : ""} found
              </p>
            )}

            {/* Report grid */}
            {filteredAvailable.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <FileText className="h-10 w-10 mx-auto mb-3 opacity-30" />
                <p className="font-medium">No reports found</p>
                <p className="text-sm mt-1">
                  Try adjusting your search or filters
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {filteredAvailable.map((report) => {
                  const isTemplate = !("isCustom" in report && report.isCustom);
                  // Find matching scheduled report for this entry
                  const sched = scheduledReports.find(
                    (s) => s.reportId === report.id && s.status === "active",
                  );
                  const scheduleInfo = sched
                    ? {
                        frequency: sched.frequency,
                        day: sched.daysOfWeek?.[0],
                        time: sched.startTime,
                        delivery: sched.format,
                        recipients: sched.recipients,
                      }
                    : undefined;
                  return (
                    <ReportGridRow
                      key={report.id}
                      name={report.name}
                      description={report.description ?? ""}
                      category={
                        "category" in report
                          ? (report.category as string)
                          : "custom"
                      }
                      formats={report.formats}
                      scheduleInfo={scheduleInfo}
                      isProtected={isTemplate}
                      isFavourited={report.isFavourited}
                      onGenerate={() => setActiveReport(report)}
                      onFavourite={() =>
                        handleToggleFavourite(report.id, isTemplate)
                      }
                      onClone={
                        canManageCustomReports && isTemplate
                          ? () => {
                              setCloneTemplate(report as ReportDefinition);
                              setCloneDialogOpen(true);
                            }
                          : undefined
                      }
                    />
                  );
                })}
              </div>
            )}
          </TabsContent>
        )}

        {/* ── Custom Reports ── */}
        {canManageCustomReports && (
          <TabsContent value="custom" className="space-y-5">
            {/* New Report button + header */}
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-base font-semibold">
                  Custom Reports Workspace
                </h2>
                <p className="text-sm text-muted-foreground">
                  Build, iterate, and publish reports. Drafts stay here until
                  you're ready to share.
                </p>
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button className="bg-teal-600 hover:bg-teal-700">
                    <Plus className="h-4 w-4 mr-2" />
                    New Report
                    <ChevronDown className="h-4 w-4 ml-2" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem
                    onClick={() => {
                      setEditingCustomReport(null);
                      setCloneSource(null);
                      setBuilderOpen(true);
                    }}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    From Scratch
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setClonePickerOpen(true)}>
                    <Copy className="h-4 w-4 mr-2" />
                    Clone From...
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* My Custom Reports */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold">
                  My Custom Reports
                </CardTitle>
                <CardDescription className="text-xs">
                  Reports you've built. Publish a report to make it available to
                  other roles.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Filters */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      value={customSearch}
                      onChange={(e) => setCustomSearch(e.target.value)}
                      placeholder="Search custom reports..."
                      className="pl-9"
                    />
                  </div>
                  <Select
                    value={customStatusFilter}
                    onValueChange={(v) =>
                      setCustomStatusFilter(v as typeof customStatusFilter)
                    }
                  >
                    <SelectTrigger className="w-full sm:w-36">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="published">Published</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={customFormat} onValueChange={setCustomFormat}>
                    <SelectTrigger className="w-full sm:w-36">
                      <SelectValue placeholder="Format" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Formats</SelectItem>
                      <SelectItem value="PDF">PDF</SelectItem>
                      <SelectItem value="Excel">Excel</SelectItem>
                      <SelectItem value="CSV">CSV</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {filteredCustomReports.length === 0 ? (
                  <div className="text-center py-10 text-muted-foreground">
                    <LayoutTemplate className="h-10 w-10 mx-auto mb-3 opacity-30" />
                    <p className="font-medium text-sm">
                      {customSearch || customStatusFilter !== "all"
                        ? "No reports match your search"
                        : "No custom reports yet"}
                    </p>
                    <p className="text-xs mt-1">
                      {!customSearch &&
                        customStatusFilter === "all" &&
                        "Create one from scratch or clone a system template above"}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {filteredCustomReports.map((report) => (
                      <ReportGridRow
                        key={report.id}
                        name={report.name}
                        description={report.description}
                        category="custom"
                        formats={report.formats}
                        isPublished={report.isPublished}
                        showManageActions={true}
                        onEdit={() => {
                          setEditingCustomReport(report);
                          setCloneSource(null);
                          setBuilderOpen(true);
                        }}
                        onDelete={() => handleDeleteCustomReport(report)}
                        onPublish={() => {
                          setPublishTarget(report);
                          setPublishDialogOpen(true);
                        }}
                        onClone={() => {
                          const dupe: CustomReport = {
                            ...report,
                            id: `custom-${Date.now()}`,
                            name: `Copy of ${report.name}`,
                            isPublished: false,
                            publishedToRoleIds: [],
                            status: "draft",
                            createdAt: new Date(),
                            updatedAt: new Date(),
                          };
                          setCustomReports((prev) => [...prev, dupe]);
                          toast.success(`"${dupe.name}" created`);
                        }}
                      />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* ── Scheduled Reports ── */}
        {canViewReports && (
          <TabsContent value="scheduled" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Automated Report Schedule</CardTitle>
                    <CardDescription>
                      Reports automatically generated and delivered on schedule
                    </CardDescription>
                  </div>
                  <Button
                    onClick={() => {
                      setEditingSchedule(null);
                      setScheduleModalOpen(true);
                    }}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Schedule
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {scheduledReports.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Clock className="h-10 w-10 mx-auto mb-3 opacity-40" />
                    <p className="font-medium mb-1">No scheduled reports</p>
                    <p className="text-sm">
                      Add a schedule to automate report delivery
                    </p>
                  </div>
                ) : (
                  <div className="space-y-1">
                    {scheduledReports.map((scheduled) => (
                      <div
                        key={scheduled.id}
                        className={`flex items-center gap-3 px-3 py-2.5 border rounded-lg transition-colors ${
                          scheduled.status === "paused"
                            ? "bg-muted/30 opacity-70"
                            : "hover:bg-accent/50"
                        }`}
                      >
                        {/* Schedule detail popover — same pattern as Available Reports */}
                        <Popover>
                          <PopoverTrigger asChild>
                            <button
                              type="button"
                              className="text-teal-600 hover:text-teal-700 transition-colors flex-shrink-0"
                              title="View schedule details"
                            >
                              <CalendarClock className="h-4 w-4" />
                            </button>
                          </PopoverTrigger>
                          <PopoverContent className="w-64 p-3" align="start">
                            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                              Schedule Details
                            </p>
                            <div className="space-y-1.5 text-sm">
                              <div className="flex items-center gap-2">
                                <Clock className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                                <span className="font-medium capitalize">
                                  {scheduled.frequency}
                                </span>
                                <span className="text-muted-foreground">
                                  —{" "}
                                  {scheduled.daysOfWeek
                                    .map(
                                      (d) =>
                                        d.charAt(0).toUpperCase() + d.slice(1),
                                    )
                                    .join(", ")}{" "}
                                  at {scheduled.startTime}
                                </span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Mail className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                                <span>{scheduled.recipients}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <FileText className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                                <span>{scheduled.format}</span>
                              </div>
                            </div>
                          </PopoverContent>
                        </Popover>

                        {/* Name + status badge */}
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <span className="font-medium text-sm truncate">
                            {scheduled.name}
                          </span>
                          <Badge
                            variant={
                              scheduled.status === "active"
                                ? "default"
                                : "secondary"
                            }
                            className="capitalize text-xs flex-shrink-0"
                          >
                            {scheduled.status}
                          </Badge>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 w-7 p-0"
                                  onClick={() => {
                                    setEditingSchedule(scheduled);
                                    setScheduleModalOpen(true);
                                  }}
                                >
                                  <Edit className="h-3.5 w-3.5" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Edit schedule</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>

                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 w-7 p-0"
                                  onClick={() =>
                                    handleTogglePause(scheduled.id)
                                  }
                                >
                                  {scheduled.status === "active" ? (
                                    <Pause className="h-3.5 w-3.5" />
                                  ) : (
                                    <Play className="h-3.5 w-3.5" />
                                  )}
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                {scheduled.status === "active"
                                  ? "Pause schedule"
                                  : "Resume schedule"}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>

                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 w-7 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                                  onClick={() =>
                                    setDeleteScheduleConfirm(scheduled.id)
                                  }
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Delete schedule</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* ── History ── */}
        {canViewReports && (
          <TabsContent value="history" className="space-y-4">
            <div className="flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/30 px-4 py-3 text-sm text-blue-800 dark:text-blue-300">
              <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span>
                Report history is retained for <strong>30 days</strong>.
                Download a copy before it expires to keep it permanently.
              </span>
            </div>
            <Card>
              <CardHeader>
                <CardTitle>Recent Report History</CardTitle>
                <CardDescription>
                  Previously generated reports available for preview and
                  download
                </CardDescription>
              </CardHeader>
              <CardContent>
                {(() => {
                  const visibleIds = new Set(visibleTemplates.map((r) => r.id));
                  const filteredHistory = reportHistory.filter((h) =>
                    visibleIds.has(h.reportId),
                  );
                  if (filteredHistory.length === 0) {
                    return (
                      <div className="text-center py-12 text-muted-foreground">
                        <FileText className="h-10 w-10 mx-auto mb-3 opacity-30" />
                        <p className="font-medium">No report history yet</p>
                        <p className="text-sm mt-1">
                          Reports you generate will appear here
                        </p>
                      </div>
                    );
                  }
                  return (
                    <div className="space-y-3">
                      {filteredHistory.map((history, index) => {
                        const matchedReport = visibleTemplates.find(
                          (r) => r.id === history.reportId,
                        );
                        const isExpiringSoon = history.expiresInDays <= 5;
                        return (
                          <div
                            key={index}
                            className={`flex items-center justify-between p-4 border rounded-lg hover:bg-accent/50 transition-colors ${
                              isExpiringSoon
                                ? "border-amber-300 bg-amber-50/50 dark:border-amber-700 dark:bg-amber-950/20"
                                : ""
                            }`}
                          >
                            <div className="flex items-center gap-4">
                              <FileText
                                className={`h-5 w-5 flex-shrink-0 ${
                                  isExpiringSoon
                                    ? "text-amber-500"
                                    : "text-muted-foreground"
                                }`}
                              />
                              <div>
                                <div className="flex items-center gap-2">
                                  <p className="font-medium">{history.name}</p>
                                  {isExpiringSoon && (
                                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 dark:bg-amber-900/40 px-2 py-0.5 text-xs font-medium text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-700">
                                      <AlertTriangle className="h-3 w-3" />
                                      Expires in {history.expiresInDays} day
                                      {history.expiresInDays !== 1 ? "s" : ""}
                                    </span>
                                  )}
                                </div>
                                <p className="text-sm text-muted-foreground">
                                  {formatDate(history.date)} · Generated by{" "}
                                  {history.user}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-4">
                              <div className="text-right">
                                <p className="text-sm font-medium">
                                  {history.format}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {history.size}
                                </p>
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  disabled={!matchedReport}
                                  onClick={() => {
                                    if (matchedReport)
                                      setActiveReport(matchedReport);
                                  }}
                                  title="Preview"
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant={
                                    isExpiringSoon ? "default" : "outline"
                                  }
                                  title="Download"
                                  onClick={() =>
                                    toast.success(
                                      `Downloading ${history.name} (${history.format}, ${history.size})…`,
                                    )
                                  }
                                >
                                  <Download className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* ── Manage ── */}
        {canManageCustomReports && (
          <TabsContent value="manage" className="space-y-6">
            {/* Role Access Control */}
            <Card>
              <CardHeader>
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-primary/10 rounded-lg mt-0.5">
                    <Shield className="h-5 w-5" />
                  </div>
                  <div className="flex-1">
                    <CardTitle>Role Access Control</CardTitle>
                    <CardDescription>
                      Control which roles can see each report in their Available
                      Reports tab. Search or filter by role to find what you
                      need quickly.
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <RoleAccessControl
                  entries={roleAccessEntries}
                  onUpdate={handleRoleAccessUpdate}
                />
                <div className="mt-4 bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-900 rounded-lg p-4">
                  <div className="flex gap-3">
                    <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-blue-900 dark:text-blue-100">
                      Role assignments are managed here. To add or edit the
                      roles themselves, go to{" "}
                      <strong>Admin → Roles &amp; Permissions</strong>.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Report Studio — global branding & settings */}
            <Card>
              <CardHeader>
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-primary/10 rounded-lg mt-0.5">
                    <Palette className="h-5 w-5" />
                  </div>
                  <div>
                    <CardTitle>Report Studio Settings</CardTitle>
                    <CardDescription>
                      Global settings for all reports — logo library, colour
                      presets, and header/footer templates. These are available
                      as options when building any custom report.
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Logo library */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="font-semibold">Logo Library</Label>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        toast.info(
                          "Logo upload — stored in memory for this session. In live mode logos are saved to your organisation's logo library and persist across sessions.",
                        )
                      }
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      Upload Logo
                    </Button>
                  </div>
                  <div className="border-2 border-dashed border-muted-foreground/20 rounded-lg p-6 text-center">
                    <ImageIcon className="h-8 w-8 mx-auto mb-2 text-muted-foreground/40" />
                    <p className="text-sm text-muted-foreground mb-1">
                      No logos uploaded yet
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Upload PNG or SVG files. Logos uploaded here become
                      available as options when building any custom report.
                    </p>
                  </div>
                </div>

                {/* Colour presets */}
                <div className="space-y-3">
                  <Label className="font-semibold">Colour Presets</Label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {BRANDING_PRESETS.map((p) => (
                      <div
                        key={p.id}
                        className="border rounded-lg p-3 text-center space-y-2"
                      >
                        <div className="flex justify-center gap-1">
                          <div
                            className="h-6 w-6 rounded-full border"
                            style={{ backgroundColor: p.primaryColour }}
                          />
                          <div
                            className="h-6 w-6 rounded-full border"
                            style={{ backgroundColor: p.accentColour }}
                          />
                        </div>
                        <p className="text-xs font-medium">{p.name}</p>
                        <p className="text-xs text-muted-foreground capitalize">
                          {p.headerStyle}
                        </p>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Custom presets and the ability to edit colours will be
                    available in a future release.
                  </p>
                </div>

                {/* Header/footer templates */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <Label className="font-semibold">Header Templates</Label>
                    <div className="space-y-2">
                      {HEADER_TEMPLATES.map((t) => (
                        <div
                          key={t.id}
                          className="flex items-start gap-3 p-3 border rounded-lg"
                        >
                          <LayoutTemplate className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-sm font-medium">{t.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {t.content}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-3">
                    <Label className="font-semibold">Footer Templates</Label>
                    <div className="space-y-2">
                      {FOOTER_TEMPLATES.map((t) => (
                        <div
                          key={t.id}
                          className="flex items-start gap-3 p-3 border rounded-lg"
                        >
                          <LayoutTemplate className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-sm font-medium">{t.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {t.content}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* ── Modals ── */}

      <ReportScheduleModal
        open={scheduleModalOpen}
        onOpenChange={(open) => {
          setScheduleModalOpen(open);
          if (!open) setEditingSchedule(null);
        }}
        editingSchedule={editingSchedule}
        onSave={handleSaveSchedule}
        availableReports={allSchedulableReports}
      />

      <ReportPreviewModal
        report={activeReport}
        open={activeReport !== null}
        onClose={() => setActiveReport(null)}
      />

      <BuilderWizard
        open={builderOpen}
        onOpenChange={(open) => {
          setBuilderOpen(open);
          if (!open) {
            setEditingCustomReport(null);
            setCloneSource(null);
          }
        }}
        editingReport={editingCustomReport}
        cloneSource={cloneSource}
        onSave={handleSaveCustomReport}
      />

      <PublishReportDialog
        open={publishDialogOpen}
        onOpenChange={setPublishDialogOpen}
        report={publishTarget}
        onPublish={handlePublish}
        onUnpublish={handleUnpublish}
      />

      <ClonePickerDialog
        open={clonePickerOpen}
        onOpenChange={setClonePickerOpen}
        templateReports={templateReports}
        customReports={customReports}
        onSelect={handleClonePickerSelect}
      />

      <CloneTemplateDialog
        open={cloneDialogOpen}
        onOpenChange={setCloneDialogOpen}
        template={cloneTemplate}
        onClone={(name, description) => {
          if (cloneTemplate) {
            handleCloneTemplate(cloneTemplate, name, description);
          }
        }}
      />

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        reportName={deleteTarget?.name ?? ""}
        isLinkedToRoles={(deleteTarget?.publishedToRoleIds?.length ?? 0) > 0}
        linkedRoleNames={deleteTargetLinkedRoles}
        onConfirm={handleConfirmDelete}
      />

      {/* Schedule delete confirmation */}
      <AlertDialog
        open={deleteScheduleConfirm !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteScheduleConfirm(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this schedule?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove the schedule. The report itself will
              not be affected — it will remain available in Available Reports.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteScheduleConfirm(null)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (deleteScheduleConfirm) {
                  handleDeleteSchedule(deleteScheduleConfirm);
                  setDeleteScheduleConfirm(null);
                }
              }}
            >
              Delete Schedule
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
